import { Typography, Box, Button, TextField, List, ListItem, ListItemText, Paper, IconButton, MenuItem, Select, FormControl, InputLabel, Chip, CircularProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Radio, RadioGroup, FormControlLabel, FormGroup, Checkbox, Tooltip } from '@mui/material';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import GeminiIcon from './components/GeminiIcon';
import TripMap from './TripMap';
import InteractiveRouteBuilder from './InteractiveRouteBuilder';
import './gemini-icon.css';
import { API_BASE_URL, API_PATH, BASE_PATH } from './config';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.GOOGLE_MAPS_API_KEY || '';

// Import actual data
import countriesCities from './data/countries_cities.json';
import airportCodes from './data/airport_codes.json';
import majorCountryCities from './data/major_country_cities.json';
import usCityStates from './data/us_city_states.json';

// Helper function to format US airport label with state
const formatUSAirportLabel = (airport) => {
  if (!airport || airport.country !== 'United States') return null;
  
  const city = airport.city || '';
  const stateInfo = usCityStates[city];
  
  if (stateInfo) {
    return `${airport.iata} - ${airport.name}, ${city}, ${stateInfo.state} (${stateInfo.code}), USA`;
  }
  
  // Fallback for cities not in our mapping
  return `${airport.iata} - ${airport.name}, ${city}, USA`;
};

// Helper function to get airport label (US or international)
const getAirportLabel = (airport) => {
  if (!airport) return '';
  
  const countryName = airport.country;
  
  if (countryName === 'United States') {
    return formatUSAirportLabel(airport);
  }
  
  // International airports
  return `${airport.iata} - ${airport.name}${airport.city ? `, ${airport.city}` : ''}${countryName ? ` (${countryName})` : ''}`;
};

// Helper function to sort airports - international airports first
const sortAirportsInternationalFirst = (airports) => {
  return airports.sort((a, b) => {
    const aIsInternational = (a.name || '').toLowerCase().includes('international');
    const bIsInternational = (b.name || '').toLowerCase().includes('international');
    
    if (aIsInternational && !bIsInternational) return -1;
    if (!aIsInternational && bIsInternational) return 1;
    return 0; // Keep original order for same category
  });
};

const majorCityNames = new Set(
  Object.values(majorCountryCities)
    .flat()
    .filter(Boolean)
    .map(name => name.trim())
);

const countriesCityNames = new Set(
  countriesCities
    .flatMap(country => (country.cities || []).map(city => city && city.trim()))
    .filter(Boolean)
);

const LOGISTIC_OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Custom Date Range Input Component (Expedia-style)
function DateRangeInput({ startDate, endDate, onChange }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [selectingStart, setSelectingStart] = useState(true);
  const containerRef = React.useRef(null);

  // Close calendar when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setTempStartDate(startDate);
        setTempEndDate(endDate);
        setSelectingStart(true);
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, startDate, endDate]);

  // Update temp dates when props change (only when calendar is closed)
  useEffect(() => {
    if (!isOpen) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [startDate, endDate, isOpen]);

  // Reset selection state when opening calendar
  useEffect(() => {
    if (isOpen) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      // Always start with selecting departure date when calendar opens
      // This makes it easier for users to change their selection
      setSelectingStart(true);
    }
  }, [isOpen, startDate, endDate]);

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return dayjs(date).format('DD MMM');
  };

  const getDisplayText = () => {
    if (startDate && endDate) {
      const start = formatDisplayDate(startDate);
      const end = formatDisplayDate(endDate);
      const duration = dayjs(endDate).diff(dayjs(startDate), 'day');
      return `${start} - ${end} (${duration} ${t('wizard_day_plural')})`;
    } else if (startDate) {
      return `${formatDisplayDate(startDate)} - ${t('date_range_input.select_return')}`;
    } else {
      return t('date_range_input.select_departure_return');
    }
  };

  const handleDateClick = (date) => {
    // Ensure we're working with a proper dayjs object
    const dayjsDate = dayjs(date);
    const dateStr = dayjsDate.format('YYYY-MM-DD');
    
    if (selectingStart) {
      // Selecting departure date
      setTempStartDate(dateStr);
      setTempEndDate(''); // Clear return date when departure changes
      setSelectingStart(false); // Now select return date
    } else {
      // Selecting return date
      if (!tempStartDate) {
        // No departure date set, set it as departure
        setTempStartDate(dateStr);
        setSelectingStart(false);
      } else if (dayjsDate.isBefore(dayjs(tempStartDate))) {
        // Selected date is before departure, treat as new departure date
        setTempStartDate(dateStr);
        setTempEndDate('');
        setSelectingStart(false);
      } else if (dayjsDate.isSame(dayjs(tempStartDate))) {
        // Same as departure date, this should be allowed for 1-day trips
        // but let's treat it as wanting to select a new departure date instead
        setTempStartDate(dateStr);
        setTempEndDate('');
        setSelectingStart(false);
      } else {
        // Valid return date (after departure)
        setTempEndDate(dateStr);
        // Both dates selected, apply and close
        onChange(tempStartDate, dateStr);
        setIsOpen(false);
        setSelectingStart(true);
      }
    }
  };

  // Add function to reset and start fresh date selection
  const handleStartNewSelection = () => {
    setTempStartDate('');
    setTempEndDate('');
    setSelectingStart(true);
  };

  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const generateCalendarDays = (monthDate) => {
    const year = monthDate.year();
    const month = monthDate.month(); // 0-based (0 = January)
    
    // Get the first day of the month and find what day of week it starts on
    const firstDayOfMonth = dayjs(new Date(year, month, 1));
    const startDayOfWeek = firstDayOfMonth.day(); // 0 = Sunday, 1 = Monday, etc.
    
    // Get the number of days in this month
    const daysInMonth = firstDayOfMonth.daysInMonth();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      const prevMonthDate = firstDayOfMonth.subtract(startDayOfWeek - i, 'day');
      const dateStr = prevMonthDate.format('YYYY-MM-DD');
      
      days.push(
        <Box
          key={`prev-${i}`}
          sx={{
            p: 1,
            minWidth: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            opacity: 0.3,
            color: 'text.disabled'
          }}
        >
          {prevMonthDate.format('D')}
        </Box>
      );
    }
    
    // Add all days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = dayjs(new Date(year, month, day));
      const dateStr = currentDate.format('YYYY-MM-DD');
      const isSelected = dateStr === tempStartDate || dateStr === tempEndDate;
      const isInRange = tempStartDate && tempEndDate && 
                       currentDate.isAfter(dayjs(tempStartDate)) && 
                       currentDate.isBefore(dayjs(tempEndDate));
      const isPast = currentDate.isBefore(dayjs(), 'day');
      
      days.push(
        <Box
          key={dateStr}
          onClick={() => !isPast && handleDateClick(currentDate)}
          sx={{
            p: 1,
            minWidth: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isPast ? 'not-allowed' : 'pointer',
            borderRadius: '4px',
            fontSize: '14px',
            bgcolor: isSelected ? 'primary.main' : isInRange ? 'primary.100' : 'transparent',
            color: isPast ? 'text.disabled' : isSelected ? 'white' : isInRange ? 'primary.main' : 'text.primary',
            border: currentDate.isSame(dayjs(), 'day') ? '2px solid' : 'none',
            borderColor: 'primary.main',
            '&:hover': !isPast ? {
              bgcolor: isSelected ? 'primary.dark' : 'primary.50'
            } : {}
          }}
        >
          {day}
        </Box>
      );
    }
    
    return days;
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        value={getDisplayText()}
        label={t('date_range_input.travel_dates')}
        required
        onClick={() => setIsOpen(!isOpen)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <IconButton onClick={() => setIsOpen(!isOpen)}>
              📅
            </IconButton>
          )
        }}
        sx={{ width: '100%', cursor: 'pointer' }}
      />
      
      {isOpen && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            p: 0,
            zIndex: 1000,
            minWidth: '320px',
            maxWidth: '400px'
          }}
          elevation={8}
        >
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'primary.50'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {selectingStart ? t('date_range_input.select_departure_date') : t('date_range_input.select_return_date')}
              </Typography>
              {(tempStartDate || startDate) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleStartNewSelection}
                  sx={{ fontSize: '11px', py: 0.5, px: 1 }}
                >
                  {t('date_range_input.new_selection')}
                </Button>
              )}
            </Box>
            {selectingStart && (startDate || tempStartDate) && (
              <Typography variant="body2" sx={{ color: 'warning.main', mt: 0.5, fontSize: '12px' }}>
                {t('date_range_input.click_to_start_new')}
              </Typography>
            )}
            {tempStartDate && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {t('date_range_input.departure_label', { date: dayjs(tempStartDate).format('DD MMM YYYY') })}
              </Typography>
            )}
            {tempStartDate && !selectingStart && (
              <Typography variant="body2" sx={{ color: 'primary.main', mt: 0.5, fontSize: '12px' }}>
                {t('date_range_input.pick_return_prompt')}
              </Typography>
            )}
          </Box>

          {/* Month Navigation */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <IconButton 
              size="small" 
              onClick={() => setCurrentMonth(prev => prev.subtract(1, 'month'))}
              disabled={currentMonth.isSame(dayjs(), 'month')}
            >
              ←
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {currentMonth.format('MMMM YYYY')}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setCurrentMonth(prev => prev.add(1, 'month'))}
            >
              →
            </IconButton>
          </Box>

          {/* Calendar */}
          <Box sx={{ p: 2 }}>
            {/* Day headers */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: 0.5,
              mb: 1
            }}>
              {[
                t('days_short.sun'), 
                t('days_short.mon'), 
                t('days_short.tue'), 
                t('days_short.wed'), 
                t('days_short.thu'), 
                t('days_short.fri'), 
                t('days_short.sat')
              ].map(day => (
                <Box key={day} sx={{ 
                  p: 1, 
                  textAlign: 'center', 
                  fontSize: '12px', 
                  fontWeight: 'bold',
                  color: 'text.secondary'
                }}>
                  {day}
                </Box>
              ))}
            </Box>
            
            {/* Calendar days */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: 0.5
            }}>
              {generateCalendarDays(currentMonth)}
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid', 
            borderColor: 'divider',
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Button 
              size="small" 
              onClick={() => {
                setTempStartDate(startDate);
                setTempEndDate(endDate);
                setIsOpen(false);
                setSelectingStart(true);
              }}
            >
              {t('date_range_input.cancel')}
            </Button>
            {tempStartDate && tempEndDate && (
              <Button 
                size="small" 
                variant="contained"
                onClick={() => {
                  onChange(tempStartDate, tempEndDate);
                  setIsOpen(false);
                  setSelectingStart(true);
                }}
              >
                {t('date_range_input.done')}
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

function TripWizard({ mode = 'create', initialData = null, onSubmit = null, onCancel = null, setIsAiWorking = null }) {
  const { t, i18n } = useTranslation();

  // Helper to set global AI working state if prop is provided
  const setGlobalAiWorking = useCallback((working) => {
    if (setIsAiWorking) {
      setIsAiWorking(working);
    }
  }, [setIsAiWorking]);

  const PACE_OPTIONS = useMemo(() => [
    {
      value: 'Relaxed',
      label: t('pace_options.relaxed.label'),
      menuLabel: t('pace_options.relaxed.menu_label'),
      title: t('pace_options.relaxed.title'),
      description: t('pace_options.relaxed.description')
    },
    {
      value: 'Moderate',
      label: t('pace_options.moderate.label'),
      menuLabel: t('pace_options.moderate.menu_label'),
      title: t('pace_options.moderate.title'),
      description: t('pace_options.moderate.description')
    },
    {
      value: 'Fast',
      label: t('pace_options.fast.label'),
      menuLabel: t('pace_options.fast.menu_label'),
      title: t('pace_options.fast.title'),
      description: t('pace_options.fast.description')
    },
  ], [t]);

  const INTEREST_CATEGORIES = useMemo(() => [
    {
      code: 'CULTURE_HISTORY',
      label: t('interest_categories.culture_history.label'),
      description: t('interest_categories.culture_history.description')
    },
    {
      code: 'FOOD_DRINK',
      label: t('interest_categories.food_drink.label'),
      description: t('interest_categories.food_drink.description')
    },
    {
      code: 'NATURE_WILDLIFE',
      label: t('interest_categories.nature_wildlife.label'),
      description: t('interest_categories.nature_wildlife.description')
    },
    {
      code: 'ACTIVE_ADVENTURE',
      label: t('interest_categories.active_adventure.label'),
      description: t('interest_categories.active_adventure.description')
    },
    {
      code: 'RELAXATION_BEACH',
      label: t('interest_categories.relaxation_beach.label'),
      description: t('interest_categories.relaxation_beach.description')
    },
    {
      code: 'ART_DESIGN',
      label: t('interest_categories.art_design.label'),
      description: t('interest_categories.art_design.description')
    },
    {
      code: 'URBAN_NIGHTLIFE',
      label: t('interest_categories.urban_nightlife.label'),
      description: t('interest_categories.urban_nightlife.description')
    },
    {
      code: 'SHOPPING',
      label: t('interest_categories.shopping.label'),
      description: t('interest_categories.shopping.description')
    },
    {
      code: 'FAMILY_FOCUSED',
      label: t('interest_categories.family_focused.label'),
      description: t('interest_categories.family_focused.description')
    },
    {
      code: 'LUXURY_HIGH_END',
      label: t('interest_categories.luxury_high_end.label'),
      description: t('interest_categories.luxury_high_end.description')
    },
    {
      code: 'WELLNESS_SPIRITUALITY',
      label: t('interest_categories.wellness_spirituality.label'),
      description: t('interest_categories.wellness_spirituality.description')
    },
    {
      code: 'OFF_BEATEN_PATH',
      label: t('interest_categories.off_beaten_path.label'),
      description: t('interest_categories.off_beaten_path.description')
    },
  ], [t]);

  const INTEREST_ITEMS = INTEREST_CATEGORIES;
  
  const INTEREST_LABEL_BY_CODE = useMemo(() => INTEREST_ITEMS.reduce((acc, item) => {
    acc[item.code] = item.label;
    return acc;
  }, {}), [INTEREST_ITEMS]);

  const INTEREST_DESC_BY_CODE = useMemo(() => INTEREST_ITEMS.reduce((acc, item) => {
    acc[item.code] = item.description;
    return acc;
  }, {}), [INTEREST_ITEMS]);

  const INTEREST_CODE_BY_LABEL = useMemo(() => INTEREST_ITEMS.reduce((acc, item) => {
    acc[item.label] = item.code;
    return acc;
  }, {}), [INTEREST_ITEMS]);

  const normalizeInterestSelections = useCallback((values = []) => {
    if (!Array.isArray(values)) return [];
    return values
      .map(value => {
        if (!value || typeof value !== 'string') return null;
        if (INTEREST_LABEL_BY_CODE[value]) return value;
        if (INTEREST_CODE_BY_LABEL[value]) return INTEREST_CODE_BY_LABEL[value];
        // Fallback: if value is a valid code (even if not in current labels map because of language switch), return it
        const validCodes = INTEREST_ITEMS.map(i => i.code);
        if (validCodes.includes(value)) return value;
        return null;
      })
      .filter(Boolean);
  }, [INTEREST_LABEL_BY_CODE, INTEREST_CODE_BY_LABEL, INTEREST_ITEMS]);

  const [step, setStep] = useState(1);
  const [isFirstTrip, setIsFirstTrip] = useState(true);
  
  // Determine mode based on props
  const actualMode = initialData ? 'update' : mode;

  const handleCancelNavigation = () => {
    if (typeof onCancel === 'function') {
      try {
        onCancel();
      } catch (e) {
        console.error('TripWizard cancel handler failed', e);
      }
    } else {
      console.warn('TripWizard cancel requested but no onCancel prop was provided');
    }
  };

  const TOTAL_STEPS = 5; // wizard steps
  const renderStepHeader = (label) => (
    <div className="tw-mb-5">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-xl tw-bg-accent/20 tw-flex tw-items-center tw-justify-center">
            <span className="tw-text-xs tw-font-bold tw-text-accent-glow">{step}</span>
          </span>
          <h2 className="tw-text-base tw-font-bold tw-text-muted-fore">{label}</h2>
        </div>
        <button
          type="button"
          onClick={handleCancelNavigation}
          aria-label={t('cancel_wizard_aria')}
          className="tw-p-1.5 tw-rounded-xl tw-text-muted tw-transition-all tw-duration-150 tw-hover:tw-text-red-400 tw-hover:tw-bg-red-500/10"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </div>
      <div className="tw-w-full tw-h-1 tw-bg-surface-3 tw-rounded-full tw-overflow-hidden">
        <div
          className="tw-h-full tw-bg-accent tw-rounded-full tw-transition-all tw-duration-500"
          style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
        />
      </div>
      <div className="tw-flex tw-justify-between tw-mt-1">
        <span className="tw-text-xs tw-text-muted tw-select-none">Step {step} / {TOTAL_STEPS}</span>
      </div>
    </div>
  );
  
  // Load user preferences from localStorage
  const getUserPreferences = () => {
    try {
      const prefs = JSON.parse(localStorage.getItem('user_trip_preferences'));
      return {
        travel_pace: prefs?.travel_pace || 'Moderate',
        trip_interests: normalizeInterestSelections(prefs?.trip_interests)
      };
    } catch (e) {
      return { travel_pace: 'Moderate', trip_interests: [] };
    }
  };

  // Save and load home airport from localStorage
  const saveHomeAirport = (airport) => {
    try {
      localStorage.setItem('user_home_airport', JSON.stringify(airport));
    } catch (e) {
      console.error('Failed to save home airport:', e);
    }
  };

  const getSavedHomeAirport = () => {
    try {
      const saved = localStorage.getItem('user_home_airport');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error('Failed to load saved home airport:', e);
      return null;
    }
  };
  
  // Form state
  const [form, setForm] = useState({
    selectedCountriesOrAirports: [],
    compiledName: '',
    name: '',
    homeAirport: null,          // Airport user departs from initially
    arrivalAirport: null,       // First airport to land in destination countries
    returnAirport: null,        // Last airport before going home
    destinationAirports: [],    // Keep for backward compatibility if needed
    trip_type: 'flexible',      // 'fixed' or 'flexible'
    duration_days: '',
    start_date: '',
    end_date: '',
    start_month: '',
    travel_pace: 'Moderate',    // Default pace
  trip_interests: [],         // Array of selected interests
    feasibilityWarning: '',     // Warning messages for trip feasibility
    start_location: '',         // Home airport for departure (for AI)
    end_location: '',           // Return airport for arrival (for AI)
    // NOTE: Steps 6-10 removed; keeping form minimal for current flow
  });

  const handleTravelPaceChange = (newPace) => {
    setForm(prev => {
      const next = { ...prev, travel_pace: newPace };
      try {
        localStorage.setItem('user_trip_preferences', JSON.stringify({
          travel_pace: newPace,
          trip_interests: next.trip_interests
        }));
      } catch (e) {
        console.error('Failed to save user_trip_preferences', e);
      }
      return next;
    });
  };

  const selectedPaceDetails = PACE_OPTIONS.find(opt => opt.value === form.travel_pace) || PACE_OPTIONS[1];

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [interestHint, setInterestHint] = useState('');
  const interestHintTimer = useRef(null);

  // Ref for tracking interactive builder completion (ref not state - must be readable synchronously in handleSubmit)
  const confirmedBlocksFromBuilderRef = useRef(null);
  const [confirmedBlocksFromBuilder, setConfirmedBlocksFromBuilder] = useState(null);
  const [builderSubmitting, setBuilderSubmitting] = useState(false); // Immediately hides builder on confirm to prevent flash

  const [logisticHistory, setLogisticHistory] = useState([]);
  const [logisticChatEntries, setLogisticChatEntries] = useState([]);
  const [currentLogisticQuestion, setCurrentLogisticQuestion] = useState(null);
  const [logisticLoading, setLogisticLoading] = useState(false);
  const [logisticError, setLogisticError] = useState('');
  const [logisticCompleted, setLogisticCompleted] = useState(false);
  const [manualFollowUpAvailable, setManualFollowUpAvailable] = useState(false);
  const [additionalLogisticQuery, setAdditionalLogisticQuery] = useState('');
  const [selectedLogisticLetters, setSelectedLogisticLetters] = useState([]);
  const [logisticCustomResponse, setLogisticCustomResponse] = useState('');
  const logisticChatContainerRef = useRef(null);
  const [logisticSummary, setLogisticSummary] = useState('');
  const [logisticSummaryLoading, setLogisticSummaryLoading] = useState(false);
  const [logisticSummaryError, setLogisticSummaryError] = useState('');

  // City context state - for displaying city info at selection level
  const [cityContexts, setCityContexts] = useState({}); // Maps "City, Country" => { why_selected, city_nature, what_to_expect }
  const [expandedCityContexts, setExpandedCityContexts] = useState({}); // Tracks which cities are expanded

  const MAX_LOGISTIC_INTERACTIONS = 5;
  const AUTO_INTERACTION_CUTOFF = 3;

  const displayedLogisticChatEntries = useMemo(() => {
    if (!currentLogisticQuestion) return logisticChatEntries;
    return logisticChatEntries.filter(entry => !(entry.sender === 'ai' && entry.id === currentLogisticQuestion.id));
  }, [logisticChatEntries, currentLogisticQuestion]);

  const plannedCountryList = useMemo(() => (
    form.selectedCountriesOrAirports
      .filter(item => item.type === 'country')
      .map(country => country.label)
  ), [form.selectedCountriesOrAirports]);

  const plannedCountriesString = useMemo(() => plannedCountryList.join(', '), [plannedCountryList]);

  const numberOfDaysString = useMemo(() => {
    if (form.trip_type === 'fixed' && form.start_date && form.end_date) {
      const start = dayjs(form.start_date);
      const end = dayjs(form.end_date);
      if (!start.isValid() || !end.isValid()) return '';
      const diffDays = end.diff(start, 'day');
      return `${diffDays} days`;
    }
    if (form.trip_type === 'flexible' && form.duration_days) {
      return `${form.duration_days} days`;
    }
    if (form.total_duration) {
      return form.total_duration;
    }
    return '';
  }, [form.trip_type, form.start_date, form.end_date, form.duration_days, form.total_duration]);

  const logisticContextSummary = useMemo(() => {
    if (!logisticHistory.length) return '';
    const answered = logisticHistory.map(entry => {
      const countryLabel = entry.country ? `${entry.country}: ` : '';
      return `${countryLabel}${entry.question} → ${entry.answer}`;
    });
    return answered.length ? answered.join(' | ') : '';
  }, [logisticHistory]);
  const destinationLabel = useMemo(() => {
    if (plannedCountriesString) return plannedCountriesString;
    if (form.arrivalAirport) return form.arrivalAirport?.city || form.arrivalAirport?.label || t('wizard_your_destination');
    return t('wizard_your_destination');
  }, [plannedCountriesString, form.arrivalAirport, t]);

  const durationLabel = useMemo(() => {
    if (numberOfDaysString) return numberOfDaysString;
    if (form.duration_days) return `${form.duration_days} ${t('wizard_day_plural')}`;
    return t('wizard_your_selected_duration');
  }, [numberOfDaysString, form.duration_days, t]);

  const formatLogisticAnswerText = useCallback((letters, note, question) => {
    const optionSummaries = (letters || [])
      .map(letter => {
        const idx = LOGISTIC_OPTION_LETTERS.indexOf(letter);
        const optionText = question?.options?.[idx];
        return optionText ? `${letter}) ${optionText}` : `${letter})`;
      })
      .filter(Boolean);

    let output = optionSummaries.join('; ');
    if (note) {
      output = output ? `${output}; Note: ${note}` : note;
    }
    return output;
  }, []);

  const queueAiQuestion = useCallback((question, { isInitial = false } = {}) => {
    const intro = isInitial
      ? t('wizard_ai_intro_initial', { destination: destinationLabel, duration: durationLabel })
      : t('wizard_ai_intro_followup');
    setLogisticChatEntries(prev => [
      ...prev,
      { 
        id: question.id, 
        sender: 'ai', 
        text: `${intro} ${question.question}`,
        options: question.options // Store options in history
      }
    ]);
  }, [destinationLabel, durationLabel, t]);

  const fetchLogisticQuestion = useCallback(async (historyEntries = [], { isManual = false } = {}) => {
    if (!plannedCountriesString || !numberOfDaysString || !form.travel_pace) {
      setLogisticError('Provide destination countries, duration, and pace to refine logistics.');
      setLogisticLoading(false);
      return;
    }

    setLogisticLoading(true);
    setGlobalAiWorking(true);
    setLogisticError('');

    try {
      const payload = {
        planned_countries: plannedCountriesString,
        number_of_days: numberOfDaysString,
        travel_pace: form.travel_pace,
        max_questions_per_call: 1,
        language: i18n.language,
        arrival_airport: form.arrivalAirport ? {
          iata: form.arrivalAirport.iata || form.arrivalAirport.value,
          name: form.arrivalAirport.name,
          city: form.arrivalAirport.city,
          country: form.arrivalAirport.country
        } : null
      };
      console.log('[Logistics] Sending payload with arrival_airport:', payload.arrival_airport);
      if (historyEntries.length) {
        payload.conversation_history = historyEntries.map(entry => ({
          question_id: entry.questionId,
          question: entry.question,
          answer: entry.answer,
          country: entry.country
        }));
      }

      const response = await fetch(`${API_PATH}/logistics-clarifications.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (response.ok && data.success && Array.isArray(data.questions)) {
        const needMoreInfo = Boolean(data.need_more_info);
        if (historyEntries.length >= 2 && !needMoreInfo) {
          setCurrentLogisticQuestion(null);
          setLogisticCompleted(true);
          setManualFollowUpAvailable(false);
          return;
        }

        if (data.questions.length > 0) {
          const nextQuestion = data.questions[0];
          setCurrentLogisticQuestion(nextQuestion);
          setSelectedLogisticLetters([]);
          setLogisticCustomResponse('');
          queueAiQuestion(nextQuestion, { isInitial: historyEntries.length === 0 && !isManual });
          setLogisticCompleted(false);
          setManualFollowUpAvailable(false);
        } else {
          setCurrentLogisticQuestion(null);
          setLogisticCompleted(true);
          setManualFollowUpAvailable(needMoreInfo && historyEntries.length < MAX_LOGISTIC_INTERACTIONS);
        }
      } else {
        const err = data.message || 'Unable to generate clarification questions.';
        setLogisticError(err);
      }
    } catch (error) {
      console.error('Failed to fetch logistic question', error);
      setLogisticError(error.message || 'Network error while generating clarifications.');
    } finally {
      setLogisticLoading(false);
      setGlobalAiWorking(false);
    }
  }, [plannedCountriesString, numberOfDaysString, form.travel_pace, queueAiQuestion, setGlobalAiWorking]);

  const generateLogisticSummary = useCallback(async () => {
    if (!plannedCountriesString || !numberOfDaysString || !form.travel_pace || !logisticHistory.length) {
      setLogisticSummary(logisticContextSummary);
      return;
    }

    console.log('Generating logistic summary...');
    setLogisticSummaryLoading(true);
    setGlobalAiWorking(true);
    setLogisticSummaryError('');

    try {
      const payload = {
        planned_countries: plannedCountriesString,
        number_of_days: numberOfDaysString,
        travel_pace: form.travel_pace,
        logistic_history: logisticHistory.map(entry => ({
          question: entry.question,
          answer: entry.answer,
          country: entry.country
        })),
        language: i18n.language
      };

      const response = await fetch(`${API_PATH}/logistics-summary.php?t=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      console.log('Logistic summary response:', data);

      if (response.ok && data.success) {
        setLogisticSummary(data.summary?.trim() || logisticContextSummary);
      } else {
        const message = data.message || 'Unable to summarize the chat.';
        console.error('Logistic summary error:', message);
        setLogisticSummaryError(message);
        setLogisticSummary(data.summary?.trim() || logisticContextSummary);
      }
    } catch (error) {
      console.error('Logistic summary network error:', error);
      const message = error?.message || 'Network error while summarizing logistics.';
      setLogisticSummaryError(message);
      setLogisticSummary(logisticContextSummary);
    } finally {
      setLogisticSummaryLoading(false);
      setGlobalAiWorking(false);
    }
  }, [plannedCountriesString, numberOfDaysString, form.travel_pace, logisticHistory, logisticContextSummary, i18n.language, setGlobalAiWorking]);

  useEffect(() => {
    if (actualMode === 'create' && step === 5) {
      setLogisticHistory([]);
      setLogisticChatEntries([]);
      setCurrentLogisticQuestion(null);
      setLogisticCompleted(false);
      setManualFollowUpAvailable(false);
      setAdditionalLogisticQuery('');
      setLogisticSummary('');
      setLogisticSummaryError('');
      setLogisticSummaryLoading(false);
      fetchLogisticQuestion([], { isManual: false });
    }
  }, [actualMode, step, fetchLogisticQuestion]);

  useEffect(() => {
    if (!logisticCompleted) {
      setLogisticSummary('');
      setLogisticSummaryError('');
      setLogisticSummaryLoading(false);
      return;
    }
    if (!logisticHistory.length) return;
    generateLogisticSummary();
  }, [logisticCompleted, logisticHistory, generateLogisticSummary]);

  useEffect(() => {
    if (!logisticChatContainerRef.current) return;
    logisticChatContainerRef.current.scrollTop = logisticChatContainerRef.current.scrollHeight;
  }, [displayedLogisticChatEntries.length, currentLogisticQuestion?.id]);

  const toggleLogisticLetter = useCallback((letter) => {
    setSelectedLogisticLetters(prev => (prev.includes(letter)
      ? prev.filter(l => l !== letter)
      : [...prev, letter]
    ));
  }, []);

  const handleLogisticResponse = () => {
    if (!currentLogisticQuestion) return;
    const trimmedNote = logisticCustomResponse.trim();
    if (!selectedLogisticLetters.length && !trimmedNote) return;

    const answerText = formatLogisticAnswerText(selectedLogisticLetters, trimmedNote, currentLogisticQuestion);
    if (!answerText) return;

    const answeredEntry = {
      questionId: currentLogisticQuestion.id,
      question: currentLogisticQuestion.question,
      country: currentLogisticQuestion.country,
      answer: answerText,
      letters: selectedLogisticLetters,
      note: trimmedNote
    };
    const updatedHistory = [...logisticHistory, answeredEntry];
    setLogisticHistory(updatedHistory);
    setLogisticChatEntries(prev => [...prev, { sender: 'user', text: answerText }]);
    setCurrentLogisticQuestion(null);
    setSelectedLogisticLetters([]);
    setLogisticCustomResponse('');

    if (updatedHistory.length >= MAX_LOGISTIC_INTERACTIONS) {
      setLogisticCompleted(true);
      setManualFollowUpAvailable(false);
      return;
    }

    if (updatedHistory.length >= AUTO_INTERACTION_CUTOFF) {
      setLogisticCompleted(true);
      setManualFollowUpAvailable(updatedHistory.length < MAX_LOGISTIC_INTERACTIONS);
      return;
    }

    fetchLogisticQuestion(updatedHistory);
  };

  const handleManualFollowUp = () => {
    if (logisticHistory.length >= MAX_LOGISTIC_INTERACTIONS) return;
    setLogisticCompleted(false);
    setManualFollowUpAvailable(false);
    fetchLogisticQuestion(logisticHistory, { isManual: true });
  };

  const handleAdditionalLogisticQuery = () => {
    const trimmed = additionalLogisticQuery.trim();
    if (!trimmed) return;
    
    setLogisticChatEntries(prev => [...prev, { sender: 'user', text: trimmed }]);
    
    const newEntry = {
      questionId: `user-followup-${Date.now()}`,
      question: 'Additional User Input',
      country: 'General',
      answer: trimmed,
      letters: [],
      note: ''
    };
    const updatedHistory = [...logisticHistory, newEntry];
    setLogisticHistory(updatedHistory);
    
    setAdditionalLogisticQuery('');
    setLogisticCompleted(false);
    setLogisticSummary('');
    
    fetchLogisticQuestion(updatedHistory, { isManual: true });
  };

  const canAdvanceLogisticsStep = !logisticLoading && (logisticCompleted || !!logisticError || logisticHistory.length > 0);

  
  // Load user preferences and saved home airport on component mount
  useEffect(() => {
    const prefs = getUserPreferences();
    const savedHome = getSavedHomeAirport();
    
    setForm(prev => ({
      ...prev,
      travel_pace: prefs.travel_pace,
      trip_interests: prefs.trip_interests
    }));
    
    setSavedHomeAirport(savedHome);
  }, []);

  // Auto-detect user location and prepopulate home airport if not already saved
  useEffect(() => {
    const detectLocationAndSetAirport = async () => {
      // Skip if user already has a saved home airport
      const savedHome = getSavedHomeAirport();
      if (savedHome) return;
      
      try {
        // Use IP-based geolocation (free service, no API key needed)
        const response = await fetch('https://ip-api.com/json/?fields=status,country,countryCode,city,lat,lon');
        const data = await response.json();
        
        if (data.status !== 'success') return;
        
        const { city, country, countryCode } = data;
        
        // Find airports in the same city or country
        // Priority: 1. Exact city match with "International", 2. City match, 3. Country major airport
        
        // First, try to find an international airport in the user's city
        let bestMatch = airportCodes.find(a => 
          a.city && a.city.toLowerCase() === city.toLowerCase() &&
          a.name && a.name.toLowerCase().includes('international') &&
          a.iata && a.iata.length === 3
        );
        
        // If no international airport in city, try any airport in the city
        if (!bestMatch) {
          bestMatch = airportCodes.find(a => 
            a.city && a.city.toLowerCase() === city.toLowerCase() &&
            a.iata && a.iata.length === 3
          );
        }
        
        // If still no match, find the main international airport in the country
        if (!bestMatch) {
          // Map of country codes to their major airport IATA codes
          const countryMainAirports = {
            'US': 'JFK', 'GB': 'LHR', 'FR': 'CDG', 'DE': 'FRA', 'NL': 'AMS',
            'IT': 'FCO', 'ES': 'MAD', 'PT': 'LIS', 'CH': 'ZRH', 'AT': 'VIE',
            'BE': 'BRU', 'DK': 'CPH', 'SE': 'ARN', 'NO': 'OSL', 'FI': 'HEL',
            'RU': 'SVO', 'JP': 'NRT', 'KR': 'ICN', 'CN': 'PEK', 'HK': 'HKG',
            'SG': 'SIN', 'TH': 'BKK', 'MY': 'KUL', 'ID': 'CGK', 'PH': 'MNL',
            'AU': 'SYD', 'NZ': 'AKL', 'CA': 'YYZ', 'MX': 'MEX', 'BR': 'GRU',
            'AR': 'EZE', 'CL': 'SCL', 'CO': 'BOG', 'PE': 'LIM', 'EG': 'CAI',
            'ZA': 'JNB', 'AE': 'DXB', 'QA': 'DOH', 'SA': 'RUH', 'IL': 'TLV',
            'TR': 'IST', 'GR': 'ATH', 'PL': 'WAW', 'CZ': 'PRG', 'HU': 'BUD',
            'IE': 'DUB', 'IN': 'DEL', 'VN': 'SGN', 'TW': 'TPE'
          };
          
          const mainIata = countryMainAirports[countryCode];
          if (mainIata) {
            bestMatch = airportCodes.find(a => a.iata === mainIata);
          }
          
          // Fallback: find any international airport in the country
          if (!bestMatch) {
            bestMatch = airportCodes.find(a => 
              a.country && a.country.toLowerCase() === country.toLowerCase() &&
              a.name && a.name.toLowerCase().includes('international') &&
              a.iata && a.iata.length === 3
            );
          }
        }
        
        if (bestMatch) {
          const airportData = {
            value: bestMatch.iata,
            label: getAirportLabel(bestMatch),
            name: bestMatch.name,
            iata: bestMatch.iata,
            icao: bestMatch.icao,
            city: bestMatch.city,
            country: bestMatch.country,
            autoDetected: true // Flag to indicate this was auto-detected
          };
          
          // Set as home airport if form doesn't already have one
          setForm(prev => {
            if (!prev.homeAirport) {
              return { ...prev, homeAirport: airportData };
            }
            return prev;
          });
          
          // Also update input display
          setHomeAirportInput(airportData.label);
        }
      } catch (error) {
        console.log('Could not auto-detect location for airport suggestion:', error);
        // Silently fail - this is just a convenience feature
      }
    };
    
    // Delay slightly to let saved preferences load first
    const timer = setTimeout(detectLocationAndSetAirport, 500);
    return () => clearTimeout(timer);
  }, []);

  // Initialize form with edit data if provided
  useEffect(() => {
    if (initialData && actualMode === 'update') {
      setForm(prev => ({
        ...prev,
        name: initialData.name || '',
        start_location: initialData.start_location || '',
        end_location: initialData.end_location || '',
        duration_days: initialData.duration_days || 7,
        travel_pace: initialData.travel_pace || 'Moderate',
        trip_interests: normalizeInterestSelections(initialData.trip_interests ? JSON.parse(initialData.trip_interests) : []),
        trip_type: initialData.start_date ? 'fixed' : 'flexible',
        start_date: initialData.start_date || '',
        end_date: initialData.end_date || '',
        start_month: initialData.month_of_year || new Date().getMonth() + 1
      }));
      
      // Parse airport information from start_location and end_location
      if (initialData.start_location) {
        // Try to extract airport info for home airport (this might be the home airport)
        const homeAirportMatch = airportCodes.find(airport => 
          initialData.start_location.includes(airport.iata) || 
          initialData.start_location.includes(airport.name)
        );
        if (homeAirportMatch) {
          setForm(prev => ({
            ...prev,
            homeAirport: {
              value: homeAirportMatch.iata,
              label: `${homeAirportMatch.name} (${homeAirportMatch.iata})`,
              name: homeAirportMatch.name,
              iata: homeAirportMatch.iata
            }
          }));
        }
      }
      
      if (initialData.end_location) {
        // Try to extract airport info for arrival/return airports
        const airportMatch = airportCodes.find(airport => 
          initialData.end_location.includes(airport.iata) || 
          initialData.end_location.includes(airport.name)
        );
        if (airportMatch) {
          setForm(prev => ({
            ...prev,
            arrivalAirport: {
              value: airportMatch.iata,
              label: `${airportMatch.name} (${airportMatch.iata})`,
              name: airportMatch.name,
              iata: airportMatch.iata
            },
            returnAirport: {
              value: airportMatch.iata,
              label: `${airportMatch.name} (${airportMatch.iata})`,
              name: airportMatch.name,
              iata: airportMatch.iata
            }
          }));
        }
      }
      
      // If route data exists, set it up for editing and extract countries
      if (initialData.route) {
        setPreviewRoute(initialData.route);
        const map = {};
        initialData.route.forEach(d => {
          map[`day-${d.day}`] = `${d.city}${d.country ? ', ' + d.country : ''}`;
        });
        setEditableDayLocations(map);
        
        // Extract unique countries from route and populate selectedCountriesOrAirports
        const routeCountries = [...new Set(initialData.route.map(d => d.country).filter(Boolean))];
        const countrySelections = routeCountries.map(country => ({
          value: country,
          label: country,
          type: 'country'
        }));
        setForm(prev => ({
          ...prev,
          selectedCountriesOrAirports: countrySelections
        }));
        updateTripName(countrySelections);
      }
    }
  }, [initialData, actualMode]);

  // Check if this is user's first trip
  useEffect(() => {
    const checkFirstTrip = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await fetch(`${API_PATH}/trips/get.php`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setIsFirstTrip(!data || data.length === 0);
          }
        } else {
          setIsFirstTrip(true); // No token = likely first time user
        }
      } catch (error) {
        console.error('Error checking trip count:', error);
        setIsFirstTrip(true); // Default to first trip on error
      }
    };
    
    if (mode === 'create') {
      checkFirstTrip();
    }
  }, [mode]);

  // Auto-derive countries when airports are selected (NEW FLOW)
  useEffect(() => {
    if (actualMode === 'create' && form.homeAirport && form.arrivalAirport && form.returnAirport) {
      const derivedCountries = autoDeriveCountriesFromAirports();
      
      // Update form with derived countries, keeping any additional countries user added
      setForm(prev => {
        const existingAdditional = prev.selectedCountriesOrAirports.filter(item => 
          item.type === 'country' && 
          !item.isInitial && // Keep only user-added countries
          !derivedCountries.find(derived => derived.label === item.label)
        );
        
        // Combine and sort by order
        const allCountries = [...derivedCountries, ...existingAdditional]
          .sort((a, b) => (a.order || 50) - (b.order || 50));
        
        return {
          ...prev,
          selectedCountriesOrAirports: allCountries
        };
      });
      
      // Update trip name based on derived countries
      updateTripName(derivedCountries);
    }
  }, [form.homeAirport, form.arrivalAirport, form.returnAirport, actualMode]);

  // Run the pace evaluation when the logistics step has completed
  useEffect(() => {
    if (actualMode !== 'create') return;
    const hasCountries = plannedCountryList.length > 0;
    const hasDuration = Boolean(numberOfDaysString) && hasCountries;

    if (step === 6 && hasDuration) {
      getTripBalanceFeedback();
    } else {
      setTripBalanceFeedback('');
      setTripBalanceStatus('');
    }
  }, [actualMode, step, numberOfDaysString, plannedCountryList, form.trip_type, logisticContextSummary]);

  const [nameOverridden, setNameOverridden] = useState(false);
  
  // Preview route and AI functionality
  const [previewRoute, setPreviewRoute] = useState(null);
  const [editableDayLocations, setEditableDayLocations] = useState({});
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [revisionInstructions, setRevisionInstructions] = useState('');
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [previewSummary, setPreviewSummary] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const MAX_HISTORY = 20;
  
  // Convert daily itinerary into city blocks (groups of consecutive same-city days)
  const groupDaysIntoBlocks = (dailyItinerary) => {
    if (!dailyItinerary || dailyItinerary.length === 0) return [];
    
    const blocks = [];
    let currentBlock = null;
    
    dailyItinerary.forEach(day => {
      // If current block is empty or same city/country, add to it
      if (!currentBlock || 
          (currentBlock.city === day.city && currentBlock.country === day.country)) {
        if (!currentBlock) {
          currentBlock = {
            city: day.city,
            country: day.country,
            start_day: day.day,
            end_day: day.day,
            duration: 1
          };
        } else {
          currentBlock.end_day = day.day;
          currentBlock.duration++;
        }
      } else {
        // Different city - save current block and start new one
        if (currentBlock) blocks.push(currentBlock);
        currentBlock = {
          city: day.city,
          country: day.country,
          start_day: day.day,
          end_day: day.day,
          duration: 1
        };
      }
    });
    
    // Add the last block
    if (currentBlock) blocks.push(currentBlock);
    
    return blocks;
  };
  
  // Dynamic placeholder for revision instructions
  const revisionPlaceholder = previewRoute && previewRoute.length ? `E.G. Extend ${previewRoute[0].city} by 1 day...` : 'E.G. Extend <first location> by 1 day...';
  
  // Generate formatted trip summary by country
  const generateFormattedSummary = () => {
    if (!previewRoute || previewRoute.length === 0) return null;
    
    // Group by country
    const countryGroups = {};
    previewRoute.forEach(day => {
      if (!countryGroups[day.country]) {
        countryGroups[day.country] = {};
      }
      if (!countryGroups[day.country][day.city]) {
        countryGroups[day.country][day.city] = [];
      }
      countryGroups[day.country][day.city].push(day.day);
    });
    
    // Format for display
    const formattedLines = [];
    Object.keys(countryGroups).forEach(country => {
      formattedLines.push(`${country}`);
      Object.keys(countryGroups[country]).forEach(city => {
        const days = countryGroups[country][city];
        const dayCount = days.length;
        const dayRange = days.length > 1 ? `${Math.min(...days)}-${Math.max(...days)}` : `${days[0]}`;
        formattedLines.push(`  ${city} days ${dayRange} - ${dayCount} ${dayCount === 1 ? 'day' : 'days'}`);
      });
    });
    
    return formattedLines;
  };
  
  // Autocomplete state  
  const [countryAirportInput, setCountryAirportInput] = useState('');
  const [countryAirportOptions, setCountryAirportOptions] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  // Timers for debounced autocomplete
  const countryAirportTimer = useRef(null);
  const homeAirportTimer = useRef(null);
  const arrivalAirportTimer = useRef(null);
  const returnAirportTimer = useRef(null);

  // Step 2 specific state
  const [homeAirportInput, setHomeAirportInput] = useState('');
  const [homeAirportOptions, setHomeAirportOptions] = useState([]);
  const [arrivalAirportInput, setArrivalAirportInput] = useState('');
  const [arrivalAirportOptions, setArrivalAirportOptions] = useState([]);
  const [returnAirportInput, setReturnAirportInput] = useState('');
  const [returnAirportOptions, setReturnAirportOptions] = useState([]);
  const [savedHomeAirport, setSavedHomeAirport] = useState(null);
  const [arrivalDropdownOpen, setArrivalDropdownOpen] = useState(false);
  const [returnDropdownOpen, setReturnDropdownOpen] = useState(false);
  const [homeDropdownOpen, setHomeDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  
  // Backward compatibility
  const [destinationInput, setDestinationInput] = useState('');
  const [destinationOptions, setDestinationOptions] = useState([]);
  
  // Gemini trip balance feedback
  const [tripBalanceFeedback, setTripBalanceFeedback] = useState('');
  const [tripBalanceStatus, setTripBalanceStatus] = useState('');
  const [isGettingFeedback, setIsGettingFeedback] = useState(false);

  // Get all airports (international airports only)
  const getAirportList = () => {
    // Major international airports that might not have "international" in name
    const majorInternationalIATA = [
      'JFK', 'LAX', 'LHR', 'CDG', 'FRA', 'AMS', 'FCO', 'BCN', 'MAD', 'LIS',
      'ZUR', 'VIE', 'BRU', 'CPH', 'ARN', 'OSL', 'HEL', 'SVO', 'LED',
      'NRT', 'ICN', 'PEK', 'PVG', 'HKG', 'SIN', 'BKK', 'KUL', 'CGK',
      'SYD', 'MEL', 'YYZ', 'YVR', 'GRU', 'EZE', 'SCL', 'BOG', 'LIM',
      'CAI', 'JNB', 'ADD', 'DXB', 'DOH', 'KWI', 'TLV', 'ATH', 'LCA', 'SOF'
    ];

    return airportCodes
        .filter(a => {
          if (!a.iata || a.iata.length !== 3 || !a.name) return false;
          const hasInternationalLabel = a.name.toLowerCase().includes('international');
          const isMajorIata = majorInternationalIATA.includes(a.iata);
          const trimmedCity = a.city ? a.city.trim() : '';
          const cityMatch = trimmedCity && (majorCityNames.has(trimmedCity) || countriesCityNames.has(trimmedCity));
          return hasInternationalLabel || isMajorIata || cityMatch;
        })
        .map(a => ({ iata: a.iata, name: a.name, icao: a.icao, city: a.city, country: a.country }));
  };

  // ICAO prefix to country mapping (common major airports)
  const icaoToCountry = {
    'LF': 'France',        // France
    'EG': 'United Kingdom', // UK
    'ED': 'Germany',       // Germany
    'EH': 'Netherlands',   // Netherlands
    'EB': 'Belgium',       // Belgium
    'EI': 'Ireland',       // Ireland
    'LE': 'Spain',         // Spain
    'LP': 'Portugal',      // Portugal
    'LI': 'Italy',         // Italy
    'LK': 'Czechia',       // Czechia (Czech Republic)
    'LO': 'Austria',       // Austria
    'LS': 'Switzerland',   // Switzerland
    'EK': 'Denmark',       // Denmark
    'ES': 'Sweden',        // Sweden
    'EN': 'Norway',        // Norway
    'EF': 'Finland',       // Finland
    'UU': 'Russia',        // Russia
    'K': 'United States',  // USA (starts with K)
    'C': 'Canada',         // Canada (starts with C)
    'MM': 'Mexico',        // Mexico
    'MU': 'Cuba',          // Cuba
    'TJ': 'Jamaica',       // Jamaica
    'SB': 'Brazil',        // Brazil
    'SA': 'Argentina',     // Argentina
    'SC': 'Chile',         // Chile
    'SK': 'Colombia',      // Colombia
    'SU': 'Uruguay',       // Uruguay
    'RJ': 'Japan',         // Japan
    'RK': 'South Korea',   // South Korea
    'ZB': 'China',         // China
    'VH': 'Australia',     // Australia
    'NZ': 'New Zealand',   // New Zealand
    'FA': 'South Africa',  // South Africa
    'HE': 'Egypt',         // Egypt
    'HL': 'Libya',         // Libya
    'DN': 'Nigeria',       // Nigeria
    'HK': 'Kenya',         // Kenya
    'OI': 'Iran',          // Iran
    'OJ': 'Jordan',        // Jordan
    'OM': 'United Arab Emirates', // UAE
    'OO': 'Lebanon',       // Lebanon
    'OS': 'Syria',         // Syria
    'OT': 'Qatar',         // Qatar
    'OY': 'Yemen',         // Yemen
    'VI': 'India',         // India
    'VT': 'India',         // India (alternate)
    'VN': 'Nepal',         // Nepal
    'VQ': 'Bhutan',        // Bhutan
    'VG': 'Bangladesh',    // Bangladesh
    'VL': 'Laos',          // Laos
    'VT': 'Thailand',      // Thailand
    'VV': 'Vietnam',       // Vietnam
    'WA': 'Indonesia',     // Indonesia
    'WB': 'Malaysia',      // Malaysia
    'WS': 'Singapore',     // Singapore
    'RC': 'Taiwan',        // Taiwan
    'RP': 'Philippines',   // Philippines
    'LL': 'Israel',        // Israel
    'LG': 'Greece',        // Greece
    'LC': 'Cyprus',        // Cyprus
    'LY': 'Serbia',        // Serbia/Montenegro region
    'LT': 'Turkey',        // Turkey
    'LR': 'Romania',       // Romania
    'LB': 'Bulgaria',      // Bulgaria
    'LH': 'Hungary',       // Hungary
    'LW': 'North Macedonia', // North Macedonia
    'LA': 'Albania',       // Albania
    'LQ': 'Bosnia and Herzegovina', // Bosnia
    'LD': 'Croatia',       // Croatia
    'LJ': 'Slovenia',      // Slovenia
    'LU': 'Moldova',       // Moldova
    'LM': 'Malta',         // Malta
    'UK': 'Ukraine',       // Ukraine
    'EP': 'Poland',        // Poland
    'EV': 'Latvia',        // Latvia
    'EE': 'Estonia',       // Estonia
    'EY': 'Lithuania',     // Lithuania
    'UM': 'Belarus',       // Belarus
    'VE': 'India',         // India (alternate)
    'VD': 'Cambodia',      // Cambodia
    'VM': 'Macau',         // Macau
    'WI': 'Indonesia',     // Indonesia (alternate)
    'WM': 'Malaysia',      // Malaysia (alternate)
  };

  // Get country from airport ICAO code
  const getCountryFromICAO = (icao) => {
    if (!icao) return null;
    
    // Check for USA (K prefix)
    if (icao.startsWith('K') && icao.length === 4) {
      return 'United States';
    }
    
    // Check for Canada (C prefix)
    if (icao.startsWith('C') && icao.length === 4) {
      return 'Canada';
    }
    
    // Check two-letter prefixes
    const prefix = icao.substring(0, 2);
    return icaoToCountry[prefix] || null;
  };

  // Get major cities from all countries
  const getMajorCitiesList = () => {
    const cities = [];
    countriesCities.forEach(country => {
      if (country.cities && Array.isArray(country.cities)) {
        country.cities.forEach(city => {
          cities.push({
            type: 'city',
            name: city,
            country: country.country,
            countryCode: country.name
          });
        });
      }
    });
    return cities;
  };

  // Helper function to create localized airport label
  const getLocalizedAirportLabel = (airport, localizedCity, localizedCountry) => {
    // Use localized city if provided, otherwise fall back to English
    const city = localizedCity || airport.city || '';
    const country = localizedCountry || airport.country || '';
    
    if (airport.country === 'United States') {
      const stateInfo = usCityStates[airport.city];
      if (stateInfo) {
        return `${airport.iata} - ${localizedCity || airport.city}, ${stateInfo.state} (${stateInfo.code}), USA`;
      }
      return `${airport.iata} - ${localizedCity || airport.city}, USA`;
    }
    
    // For international airports, show: IATA - LocalizedCity (LocalizedCountry)
    return `${airport.iata} - ${city}${country ? ` (${country})` : ''}`;
  };

  // Helper function to fetch multi-language autocomplete results
  // Backend returns ready-to-display results, we just pass them through
  const fetchMultilangResults = async (value, resultType = 'all') => {
    try {
      const lang = i18n?.language || 'en';
      const typeParam = resultType ? `&type=${encodeURIComponent(resultType)}` : '';
      const response = await fetch(`${API_BASE_URL}/autocomplete.php?q=${encodeURIComponent(value)}&lang=${lang}${typeParam}`);
      if (response.ok) {
        const backendResults = await response.json();
        const results = [];
        const addedItems = new Set();
        
        // Process backend results in order - they are already prioritized
        backendResults.forEach((item, index) => {
          const placeId = item.place_id || '';
          
          // Handle AIRPORT_ results (from country expansion) - format: IATA - City - Country
          if (placeId.startsWith('AIRPORT_')) {
            if (!addedItems.has(placeId)) {
              addedItems.add(placeId);
              results.push({
                type: 'airport',
                label: item.label || item.description,
                value: item.iata,
                icao: item.icao || '',
                name: item.name || '',
                city: item.city || '',
                country: item.country || '',
                iata: item.iata,
                _backendOrder: index
              });
            }
          }
          // Handle IATA exact matches - backend provides full airport details
          else if (placeId.startsWith('IATA_')) {
            if (!addedItems.has(placeId)) {
              addedItems.add(placeId);
              results.push({
                type: 'airport',
                label: item.label || item.description,
                value: item.iata,
                icao: item.icao || '',
                name: item.name || '',
                city: item.city || '',
                country: item.country || '',
                iata: item.iata,
                _backendOrder: index // Preserve backend order
              });
            }
          }
          // Handle metro codes - backend provides all info
          else if (placeId.startsWith('METRO_')) {
            if (!addedItems.has(placeId)) {
              addedItems.add(placeId);
              results.push({
                type: 'metro',
                label: item.label || item.description,
                value: item.metro_code || item.value,
                metro_code: item.metro_code,
                airports: item.airports || [],
                city: item.city || '',
                country: item.country || '',
                countryCode: item.countryCode || '',
                iata: item.metro_code,
                _backendOrder: index
              });
            }
          }
          // Handle multilang cities - backend provides airport list
          else if (placeId.startsWith('MULTILANG_CITY_')) {
            if (!addedItems.has(placeId)) {
              addedItems.add(placeId);
              // If backend provides airports, add them individually
              if (item.airports && item.airports.length > 0) {
                item.airports.forEach((airport, airportIndex) => {
                  const airportKey = 'airport_' + airport.iata;
                  if (!addedItems.has(airportKey)) {
                    addedItems.add(airportKey);
                    results.push({
                      type: 'airport',
                      label: airport.label || `${airport.iata} - ${airport.name}${airport.country ? ', ' + airport.country : ''}`,
                      value: airport.iata,
                      icao: airport.icao || '',
                      name: airport.name || '',
                      city: airport.city || item.english_name || '',
                      country: airport.country || item.country || '',
                      iata: airport.iata,
                      _backendOrder: index + (airportIndex * 0.1) // Keep airport order within city
                    });
                  }
                });
              } else {
                // Fallback: add as city type
                results.push({
                  type: 'city',
                  label: item.label || item.description,
                  value: item.english_name || item.description.split(',')[0],
                  country: item.country || '',
                  englishName: item.english_name,
                  _backendOrder: index
                });
              }
            }
          }
          // Handle multilang countries
          else if (placeId.startsWith('MULTILANG_COUNTRY_')) {
            if (!addedItems.has(placeId)) {
              addedItems.add(placeId);
              results.push({
                type: 'country',
                label: item.description,
                value: item.countryCode || item.english_name,
                englishName: item.english_name,
                _backendOrder: index
              });
            }
          }
          // Handle LOCAL_CITY results (from countries_cities.json)
          else if (placeId.startsWith('LOCAL_CITY_')) {
            if (!addedItems.has(placeId)) {
              addedItems.add(placeId);
              results.push({
                type: 'city',
                label: item.description,
                value: item.description.split(',')[0],
                country: item.description.includes(',') ? item.description.split(',')[1].trim() : '',
                _backendOrder: index
              });
            }
          }
          // Handle LOCAL_COUNTRY results
          else if (placeId.startsWith('LOCAL_COUNTRY_')) {
            if (!addedItems.has(placeId)) {
              addedItems.add(placeId);
              results.push({
                type: 'country',
                label: item.description,
                value: item.description,
                _backendOrder: index
              });
            }
          }
          // Handle Google Places results (ChIJ... format)
          else if (placeId.startsWith('ChIJ')) {
            if (!addedItems.has(placeId)) {
              addedItems.add(placeId);
              // Google Places results - add as generic location
              results.push({
                type: 'location',
                label: item.description,
                value: item.description,
                _backendOrder: index
              });
            }
          }
        });
        
        // Results are already in backend priority order
        return results;
      }
    } catch (error) {
      console.error('Error fetching multi-language autocomplete:', error);
    }
    return [];
  };

  // Enhanced autocomplete handler - uses backend for all results
  const handleCountryAirportInput = async (e) => {
    const value = e.target.value;
    setCountryAirportInput(value);

    // Debounce the heavy filtering work
    if (countryAirportTimer.current) clearTimeout(countryAirportTimer.current);
    countryAirportTimer.current = setTimeout(async () => {
      if (value.length < 2) {
        setCountryAirportOptions([]);
        return;
      }

      // Get results from backend - they are already prioritized correctly
      const backendResults = await fetchMultilangResults(value);
      
      // Backend results are in correct order, just use them directly
      // Sort by _backendOrder to preserve backend ordering
      const sortedResults = backendResults.sort((a, b) => 
        (a._backendOrder ?? 999) - (b._backendOrder ?? 999)
      );
      
      setCountryAirportOptions(sortedResults.slice(0, 20));
    }, 150);
  };

  // Handle selection
  const handleSelectCountryAirport = (option) => {
    // For Step 1 (country selection), we only add countries to the list
    // If user selects a city or airport, we add the parent country instead
    let countryToAdd = null;
    
    if (option.type === 'country') {
      // Direct country selection
      countryToAdd = option;
    } else if (option.type === 'city') {
      // City selected - find and add the parent country
      const country = countriesCities.find(c => c.name === option.value);
      if (country) {
        countryToAdd = {
          type: 'country',
          label: country.country,
          value: country.name
        };
      }
    } else if (option.type === 'airport') {
      // Airport selected - find and add the parent country
      const airport = getAirportList().find(a => a.iata === option.value);
      if (airport && airport.icao) {
        const countryName = airport.country || getCountryFromICAO(airport.icao);
        if (countryName) {
          // Find the country in our countries data to get the proper code
          const country = countriesCities.find(c => 
            c.country.toLowerCase() === countryName.toLowerCase()
          );
          if (country) {
            countryToAdd = {
              type: 'country',
              label: country.country,
              value: country.name
            };
          }
        }
      }
    }

    // Check if this country is already selected
    if (countryToAdd) {
      const isAlreadySelected = form.selectedCountriesOrAirports.some(selected => 
        selected.type === 'country' && selected.label === countryToAdd.label
      );

      if (isAlreadySelected) {
        // Country already selected - just clear the input without adding
        setCountryAirportInput('');
        setCountryAirportOptions([]);
        return;
      }

      // Add the country in the correct position
      // New countries should be inserted between arrival and return countries
      const newCountry = {
        ...countryToAdd,
        isInitial: false, // User-added countries are removable
        order: 50 // Default order for user-added countries
      };
      
      // Sort countries by order: home (0), arrival (1), user-added (50), return (999)
      const newSelections = [...form.selectedCountriesOrAirports, newCountry]
        .sort((a, b) => (a.order || 50) - (b.order || 50));
      
      setForm(prev => ({
        ...prev,
        selectedCountriesOrAirports: newSelections
      }));
      
      // Update trip name
      updateTripName(newSelections);
    }

    setCountryAirportInput('');
    setCountryAirportOptions([]);
    setCountryDropdownOpen(false);
  };

  // Step 1: Home airport search handler - uses backend for all results
  const handleHomeAirportInput = async (e) => {
    const value = e.target.value;
    setHomeAirportInput(value);

    // Debounce
    if (homeAirportTimer.current) clearTimeout(homeAirportTimer.current);
    homeAirportTimer.current = setTimeout(async () => {
      // Only show suggestions after 2+ characters (for multi-language support)
      if (value.length < 2) {
        setHomeAirportOptions([]);
        return;
      }

      // Get results from backend - request AIRPORTS ONLY
      const backendResults = await fetchMultilangResults(value, 'airports');
      
      // Expand countries to show their airports, but DO NOT show the country itself
      const expandedResults = [];
      backendResults.forEach(r => {
        if (r.is_country && r.airports && r.airports.length > 0) {
          // Add country's airports as selectable items (NOT the country)
          r.airports.forEach(airport => {
            expandedResults.push({
              type: 'airport',
              label: airport.label,
              value: airport.iata,
              iata: airport.iata,
              name: airport.name,
              city: airport.city,
              country: airport.country,
              icao: airport.icao
            });
          });
        } else if (r.iata && r.iata.length === 3) {
          // Regular airport result
          expandedResults.push(r);
        }
        // Skip countries without airports or non-airport results
      });
      
      // Sort: international airports first, then alphabetical by label
      const sortedResults = expandedResults.sort((a, b) => {
        const aIntl = /international|intl|int'l/i.test(a.name || a.label || '');
        const bIntl = /international|intl|int'l/i.test(b.name || b.label || '');
        if (aIntl && !bIntl) return -1;
        if (!aIntl && bIntl) return 1;
        return (a.label || '').localeCompare(b.label || '');
      });
      
      setHomeAirportOptions(sortedResults.slice(0, 25));
    }, 150);
  };

  // Step 1: Home airport selection handler
  const handleSelectHomeAirport = (airport) => {
    // Validate that the airport has a valid IATA code
    if (!airport || !airport.iata || airport.iata.length !== 3) {
      console.error('Invalid airport selection: no IATA code', airport);
      alert(t('wizard_error_invalid_airport') || 'Please select an airport with a valid airport code');
      return;
    }
    
    setForm(prev => ({
      ...prev,
      homeAirport: airport
      // NOTE: home airport should NOT set start_location - that's for the arrival destination
    }));
    setHomeAirportInput('');
    setHomeAirportOptions([]);
    setHomeDropdownOpen(false);
    
    // Save home airport to localStorage for future trips
    saveHomeAirport(airport);
    setSavedHomeAirport(airport);
  };

  // Quick select saved home airport
  const handleSelectSavedHomeAirport = () => {
    if (savedHomeAirport) {
      handleSelectHomeAirport(savedHomeAirport);
    }
  };

  // Step 1: Arrival airport search handler - uses backend for all results
  const handleArrivalAirportInput = async (e) => {
    const value = e.target.value;
    setArrivalAirportInput(value);

    if (arrivalAirportTimer.current) clearTimeout(arrivalAirportTimer.current);
    arrivalAirportTimer.current = setTimeout(async () => {
      // Only show suggestions after 2+ characters (for multi-language support)
      if (value.length < 2) {
        setArrivalAirportOptions([]);
        return;
      }

      const homeCountry = form.homeAirport ? (form.homeAirport.country || getCountryFromICAO(form.homeAirport.icao)) : null;

      // Get results from backend - request AIRPORTS ONLY
      const backendResults = await fetchMultilangResults(value, 'airports');
      
      // Expand countries to show their airports, but DO NOT show the country itself
      const expandedResults = [];
      backendResults.forEach(r => {
        if (r.is_country && r.airports && r.airports.length > 0) {
          // Add country's airports as selectable items (NOT the country)
          r.airports.forEach(airport => {
            expandedResults.push({
              type: 'airport',
              label: airport.label,
              value: airport.iata,
              iata: airport.iata,
              name: airport.name,
              city: airport.city,
              country: airport.country,
              icao: airport.icao
            });
          });
        } else if (r.iata && r.iata.length === 3) {
          // Regular airport result
          expandedResults.push(r);
        }
        // Skip countries without airports or non-airport results
      });
      
      // Filter to ensure all results have valid IATA codes and exclude home country
      let filteredResults = expandedResults.filter(r => r.iata && r.iata.length === 3);
      if (homeCountry) {
        filteredResults = filteredResults.filter(r => r.country !== homeCountry);
      }
      
      // Sort: international airports first, then alphabetical by label
      const sortedResults = filteredResults.sort((a, b) => {
        const aIntl = /international|intl|int'l/i.test(a.name || a.label || '');
        const bIntl = /international|intl|int'l/i.test(b.name || b.label || '');
        if (aIntl && !bIntl) return -1;
        if (!aIntl && bIntl) return 1;
        return (a.label || '').localeCompare(b.label || '');
      });
      
      setArrivalAirportOptions(sortedResults.slice(0, 25));
    }, 150);
  };

  // Show all available arrival airports when field is focused
  const handleArrivalAirportFocus = () => {
    setArrivalDropdownOpen(true);
    handleArrivalAirportInput({ target: { value: arrivalAirportInput } });
  };

  // Step 1: Arrival airport selection handler
  const handleSelectArrivalAirport = (airport) => {
    // Validate that the airport has a valid IATA code
    if (!airport || !airport.iata || airport.iata.length !== 3) {
      console.error('Invalid airport selection: no IATA code', airport);
      alert(t('wizard_error_invalid_airport') || 'Please select an airport with a valid airport code');
      return;
    }
    
    setForm(prev => ({
      ...prev,
      arrivalAirport: airport,
      returnAirport: prev.isMultiCity ? null : airport,  // Auto-set for round trip, null for multi-city
      start_location: airport ? `${airport.name} (${airport.iata})` : '',  // Trip STARTS at arrival destination
      end_location: (!prev.isMultiCity && airport) ? `${airport.name} (${airport.iata})` : ''     // Same as start for round trip
    }));
    setArrivalAirportInput('');
    setArrivalAirportOptions([]);
    setArrivalDropdownOpen(false);
    
    // Clear return airport input for multi-city
    setReturnAirportInput('');
  };

  // Step 1: Return airport search handler (specific 3-letter pattern)
  const handleReturnAirportInput = async (e) => {
    const value = e.target.value;
    setReturnAirportInput(value);

    if (returnAirportTimer.current) clearTimeout(returnAirportTimer.current);
    returnAirportTimer.current = setTimeout(async () => {
      // Only show suggestions after 2+ characters (for multi-language support)
      if (value.length < 2) {
        setReturnAirportOptions([]);
        return;
      }

      const homeAirportIATA = form.homeAirport ? form.homeAirport.iata : null;

      // Get results from backend - request AIRPORTS ONLY
      const backendResults = await fetchMultilangResults(value, 'airports');
      
      // Expand countries to show their airports, but DO NOT show the country itself
      const expandedResults = [];
      backendResults.forEach(r => {
        if (r.is_country && r.airports && r.airports.length > 0) {
          // Add country's airports as selectable items (NOT the country)
          r.airports.forEach(airport => {
            expandedResults.push({
              type: 'airport',
              label: airport.label,
              value: airport.iata,
              iata: airport.iata,
              name: airport.name,
              city: airport.city,
              country: airport.country,
              icao: airport.icao
            });
          });
        } else if (r.iata && r.iata.length === 3) {
          // Regular airport result
          expandedResults.push(r);
        }
        // Skip countries without airports or non-airport results
      });
      
      // Filter to ensure all results have valid IATA codes and exclude home airport
      let filteredResults = expandedResults.filter(r => r.iata && r.iata.length === 3);
      if (homeAirportIATA) {
        filteredResults = filteredResults.filter(r => r.iata !== homeAirportIATA);
      }
      
      // Sort: international airports first, then alphabetical by label
      const sortedResults = filteredResults.sort((a, b) => {
        const aIntl = /international|intl|int'l/i.test(a.name || a.label || '');
        const bIntl = /international|intl|int'l/i.test(b.name || b.label || '');
        if (aIntl && !bIntl) return -1;
        if (!aIntl && bIntl) return 1;
        return (a.label || '').localeCompare(b.label || '');
      });
      
      setReturnAirportOptions(sortedResults.slice(0, 25));
    }, 150);
  };

  // Show all available return airports when field is focused
  const handleReturnAirportFocus = () => {
    setReturnDropdownOpen(true);
    handleReturnAirportInput({ target: { value: returnAirportInput } });
  };

  // Show all available home airports when field is focused
  const handleHomeAirportFocus = () => {
    setHomeDropdownOpen(true);
    handleHomeAirportInput({ target: { value: homeAirportInput } });
  };

  // Show all available countries/airports when field is focused
  const handleCountryAirportFocus = () => {
    setCountryDropdownOpen(true);
    handleCountryAirportInput({ target: { value: countryAirportInput } });
  };

  // Step 1: Return airport selection handler
  const handleSelectReturnAirport = (airport) => {
    // Validate that the airport has a valid IATA code
    if (!airport || !airport.iata || airport.iata.length !== 3) {
      console.error('Invalid airport selection: no IATA code', airport);
      alert(t('wizard_error_invalid_airport') || 'Please select an airport with a valid airport code');
      return;
    }
    
    setForm(prev => ({
      ...prev,
      returnAirport: airport,
      end_location: airport ? `${airport.name} (${airport.iata})` : ''
    }));
    setReturnAirportInput('');
    setReturnAirportOptions([]);
    setReturnDropdownOpen(false);
  };

  // Auto-derive countries from selected airports (exclude home country)
  const autoDeriveCountriesFromAirports = () => {
    const derivedCountries = [];
    const homeCountry = form.homeAirport ? (form.homeAirport.country || getCountryFromICAO(form.homeAirport.icao)) : null;
    
    // Skip home country - only add destination countries
    
    // Add arrival airport country
    if (form.arrivalAirport) {
      const arrivalCountry = form.arrivalAirport.country || getCountryFromICAO(form.arrivalAirport.icao);
      if (arrivalCountry && arrivalCountry !== homeCountry) {
        const countryOption = {
          value: arrivalCountry.toLowerCase().replace(/\s+/g, '_'),
          label: arrivalCountry,
          type: 'country',
          isInitial: true, // Mark as non-removable and non-draggable
          order: 1
        };
        derivedCountries.push(countryOption);
      }
    }
    
    // For multi-city: Add return airport country at the end if different
    if (form.isMultiCity && form.returnAirport && form.returnAirport.value !== form.arrivalAirport?.value) {
      const returnCountry = form.returnAirport.country || getCountryFromICAO(form.returnAirport.icao);
      if (returnCountry && returnCountry !== homeCountry && 
          !derivedCountries.find(c => c.label === returnCountry)) {
        const countryOption = {
          value: returnCountry.toLowerCase().replace(/\s+/g, '_'),
          label: returnCountry,
          type: 'country',
          isInitial: true, // Mark as non-removable and non-draggable
          order: 999 // Will be placed at end after user-added countries
        };
        derivedCountries.push(countryOption);
      }
    }
    
    return derivedCountries;
  };

  // Get trip balance feedback from Gemini API
  const getTripBalanceFeedback = async () => {
    if (isGettingFeedback) return;

    if (!plannedCountriesString || !numberOfDaysString || !form.travel_pace) {
      setTripBalanceFeedback('');
      setTripBalanceStatus('');
      return;
    }

    const numberOfCountries = plannedCountryList.length;
    const travelDays = numberOfCountries > 0 ? numberOfCountries + 1 : 0;
    const summaryToUse = logisticSummary || logisticContextSummary;
    const logisticsNote = summaryToUse ? `Logistical context: ${summaryToUse}.

` : '';

    const prompt = `Act as a professional travel planner. Evaluate if the trip fits the traveler's selected pace.

Trip: ${numberOfDaysString} total days (${travelDays} travel), visiting ${plannedCountriesString}. Pace: ${form.travel_pace}.

${logisticsNote}Rules:
Each country has 2 travel days.
Sightseeing days per city: Relaxed=4, Moderate=3, Rushed=2.
At least 1 city per country.
If total days < countries × (2 + pace days) → rushed (cannot fit even 1 city per country).
If total days ≥ countries × (2 + pace days) → valid.
If enough for ~3 cities/country → relaxed.
Otherwise → moderate.

A well-balanced trip allows realistic coverage, rest, and enjoyment.
Consider if selected pace matches the plan; explain if not.

Return only JSON:
{
  "status": "excellent|relaxed|moderate|rushed",
  "message": "≤60 words explaining reasoning, expected #cities possible per country, and minor adjustments if needed"
}`;

    
    setIsGettingFeedback(true);
    setGlobalAiWorking(true);
    
    try {
      const response = await fetch(`${API_PATH}/trip-balance.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: prompt,
          language: i18n.language
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.response) {
          try {
            // Clean the response by removing markdown code blocks
            let cleanResponse = data.response.trim();
            
            // Remove markdown code blocks (```json and ```)
            cleanResponse = cleanResponse.replace(/^```json\s*/i, '').replace(/\s*```$/, '');
            cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
            cleanResponse = cleanResponse.trim();
            
            
            // Try to parse JSON response
            const aiResponse = JSON.parse(cleanResponse);
            if (aiResponse.status && aiResponse.message) {
              setTripBalanceStatus(aiResponse.status);
              setTripBalanceFeedback(aiResponse.message);
            } else {
              throw new Error('Invalid JSON structure');
            }
          } catch (jsonError) {
            // Fallback: treat as plain text and extract status from content
            
            const responseText = data.response.trim().toLowerCase();
            let status = 'unknown';
            
            // Enhanced status detection with multiple patterns
            if (responseText.includes('excellent') || responseText.includes('perfect') || responseText.includes('ideal')) {
              status = 'excellent';
            } else if (responseText.includes('moderate') || responseText.includes('balanced') || responseText.includes('appropriate')) {
              status = 'moderate';
            } else if (responseText.includes('relaxed') || responseText.includes('comfortable') || responseText.includes('leisurely') || responseText.includes('plenty of time')) {
              status = 'relaxed';
            } else if (responseText.includes('rushed') || responseText.includes('tight') || responseText.includes('insufficient') || responseText.includes('not enough') || responseText.includes('too many') || responseText.includes('packed')) {
              status = 'rushed';
            }
            
            setTripBalanceStatus(status);
            setTripBalanceFeedback(data.response.trim());
          }
        } else {
          setTripBalanceFeedback('Unable to get trip balance feedback');
          setTripBalanceStatus('');
        }
      } else {
        setTripBalanceFeedback('Unable to get trip balance feedback');
        setTripBalanceStatus('');
      }
    } catch (error) {
      console.error('AI Network Error:', error);
      setTripBalanceFeedback('Unable to get trip balance feedback');
      setTripBalanceStatus('');
    } finally {
      setIsGettingFeedback(false);
      setGlobalAiWorking(false);
    }
  };

  // Step 2: Destination airport search handler (only countries from Step 1)
  const handleDestinationInput = (e) => {
    const value = e.target.value;
    setDestinationInput(value);
    
    // Get list of selected country names from Step 1
    const selectedCountryNames = form.selectedCountriesOrAirports
      .filter(item => item.type === 'country')
      .map(item => item.label.toLowerCase());
    
    if (selectedCountryNames.length === 0) {
      setDestinationOptions([]);
      return;
    }
    
    const lcInput = value.toLowerCase();
    
    // Filter airports to only those in selected countries
    const airports = getAirportList().filter(a => {
      // Check if airport is in one of the selected countries
      const countryName = getCountryFromICAO(a.icao);
      if (!countryName) return false;
      
      const isInSelectedCountry = selectedCountryNames.includes(countryName.toLowerCase());
      if (!isInSelectedCountry) return false;
      
      // If no input, show all airports from selected countries
      if (value.length === 0) return true;
      
      // If input provided, filter by search term
      const nameLc = a.name.toLowerCase();
      const iataLc = a.iata.toLowerCase();
      return iataLc.includes(lcInput) || nameLc.includes(lcInput);
    });
    
    const results = airports.slice(0, 20).map(a => {
      const countryName = getCountryFromICAO(a.icao);
      return {
        type: 'airport',
        label: getAirportLabel({ ...a, country: countryName }),
        value: a.iata,
        icao: a.icao,
        name: a.name,
        city: a.city,
        country: countryName,
        iata: a.iata
      };
    });
    
    setDestinationOptions(results);
  };

  // Show all available airports when destination field is focused
  const handleDestinationFocus = () => {
    if (destinationInput.length === 0) {
      handleDestinationInput({ target: { value: '' } });
    }
  };

  // Step 2: Destination airport selection handler
  const handleSelectDestinationAirport = (airport) => {
    // Check if already selected
    const isAlreadySelected = form.destinationAirports.some(dest => 
      dest.value === airport.value
    );
    
    if (isAlreadySelected) {
      setDestinationInput('');
      setDestinationOptions([]);
      return;
    }

    setForm(prev => ({
      ...prev,
      destinationAirports: [...prev.destinationAirports, airport]
    }));
    setDestinationInput('');
    setDestinationOptions([]);
  };

  // Step 2: Remove destination airport
  const removeDestinationAirport = (indexToRemove) => {
    setForm(prev => ({
      ...prev,
      destinationAirports: prev.destinationAirports.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  const removeCountryOrAirport = (indexToRemove) => {
    setForm(prev => ({
      ...prev,
      selectedCountriesOrAirports: prev.selectedCountriesOrAirports.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  // Update trip name based on selections
  const updateTripName = (selections) => {
    const parts = selections.map(opt => {
      if (opt.type === 'country') {
        return opt.label;
      } else if (opt.type === 'city') {
        return opt.cityName;
      } else if (opt.type === 'airport') {
        return opt.value; // IATA code
      }
      return opt.label;
    });
    const compiled = parts.length > 0 ? `${parts.join(', ')}` : '';
    setForm(prev => ({
      ...prev,
      compiledName: compiled,
      name: (prev.name === prev.compiledName || !prev.name) ? compiled : prev.name
    }));
  };

  // Limit wizard to 6 steps to include the new logistics/clarification stage
  const nextStep = () => setStep(s => Math.min(s + 1, 7));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const MIN_STEP3_INTERESTS = 3;
  const selectedInterestCount = form.trip_interests.length;
  const hasSelectedPace = Boolean(form.travel_pace);
  const canAdvanceStep3 = hasSelectedPace && selectedInterestCount >= MIN_STEP3_INTERESTS;
  const step3ValidationMessage = !hasSelectedPace
    ? t('wizard_validation.select_pace')
    : selectedInterestCount < MIN_STEP3_INTERESTS
      ? t('wizard_validation.select_interests', { count: MIN_STEP3_INTERESTS })
      : '';

  const tripTypeIsFixed = form.trip_type === 'fixed';
  const tripTypeIsFlexible = form.trip_type === 'flexible';
  const hasFixedDates = Boolean(form.start_date && form.end_date);
  const hasFlexibleDuration = Boolean(form.start_month && form.duration_days);
  const canAdvanceStep4 = tripTypeIsFixed
    ? hasFixedDates
    : tripTypeIsFlexible
      ? hasFlexibleDuration
      : false;
  const step4ValidationMessage = tripTypeIsFixed
    ? (!hasFixedDates ? t('wizard_validation.select_dates') : '')
    : tripTypeIsFlexible
      ? (!hasFlexibleDuration ? t('wizard_validation.select_month_duration') : '')
      : t('wizard_validation.select_trip_type');

  const handleStep3Next = () => {
    if (canAdvanceStep3) {
      nextStep();
    }
  };

  const handleStep4Next = () => {
    if (canAdvanceStep4) {
      nextStep();
    }
  };

  const tripStartMoment = form.start_date ? dayjs(form.start_date) : null;
  const tripEndMoment = form.end_date ? dayjs(form.end_date) : null;
  const hasFixedCalendarDates = tripTypeIsFixed && tripStartMoment && tripStartMoment.isValid() && tripEndMoment && tripEndMoment.isValid();
  const formatSegmentDateRange = (startDay, length) => {
    if (!hasFixedCalendarDates || !tripStartMoment) return '';
    const safeLength = Math.max(length, 1);
    const startDate = tripStartMoment.add(startDay - 1, 'day');
    const endDate = startDate.add(safeLength - 1, 'day');
    const startText = startDate.format('D/MMM');
    const endText = endDate.format('D/MMM');
    return startText === endText ? startText : `${startText} - ${endText}`;
  };

  // Handle interest selection/deselection
  const handleInterestChange = (interest) => {
    setForm(prev => {
      const interests = prev.trip_interests.includes(interest)
        ? prev.trip_interests.filter(i => i !== interest)
        : [...prev.trip_interests, interest];
      // Always update user profile preference
      localStorage.setItem('user_trip_preferences', JSON.stringify({
        travel_pace: prev.travel_pace,
        trip_interests: interests
      }));
      return { ...prev, trip_interests: interests };
    });
    showInterestHint(INTEREST_DESC_BY_CODE[interest]);
  };

  // (removed main_interest single-select handler; interests are now multi-select tiles)

  // Helper function to generate month options
  const generateMonthOptions = (months = 24) => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < months; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
      out.push({ value: val, label });
    }
    return out;
  };

  // Detect touch / mobile devices where the native date picker is preferable
  const isMobileDateInput = (() => {
    if (typeof window === 'undefined') return false;
    try {
      return ('ontouchstart' in window) || (navigator && navigator.maxTouchPoints && navigator.maxTouchPoints > 0) || (window.matchMedia && window.matchMedia('(pointer:coarse)').matches);
    } catch (e) {
      return false;
    }
  })();

  const showInterestHint = (text) => {
    if (!text) return;
    setInterestHint(text);
    if (interestHintTimer.current) {
      clearTimeout(interestHintTimer.current);
    }
    interestHintTimer.current = setTimeout(() => {
      setInterestHint('');
      interestHintTimer.current = null;
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (interestHintTimer.current) {
        clearTimeout(interestHintTimer.current);
      }
    };
  }, []);

  // Helper: format airport label like `TLV (Israel)` or fallback to name/IATA
  const formatAirportLabel = (airport) => {
    if (!airport) return '';
    // If a string was provided (start_location/end_location), try to use it directly
    if (typeof airport === 'string') {
      const s = airport.trim();
      // If it already contains a country in parentheses, return as-is
      if (s.match(/\([^)]+\)/)) return s;
      // Try to extract an IATA code and resolve it
      const iataMatch = s.match(/([A-Z]{3})/);
      if (iataMatch) {
        const code = iataMatch[1];
        const found = airportCodes.find(a => a.iata === code);
        if (found) {
          // Use country directly from airport data first, fallback to ICAO prefix
          const country = found.country || (() => {
            const icao = found.icao || '';
            const prefix = (icao && icao.length >= 2) ? icao.substring(0,2).toUpperCase() : '';
            const icaoToCountryMap = {
              'LL': 'Israel',
              'VT': 'Thailand',
              'EG': 'United Kingdom',
              'LF': 'France',
              'ED': 'Germany',
              'EH': 'Netherlands',
              'EB': 'Belgium',
              'EI': 'Ireland',
              'LE': 'Spain',
              'LP': 'Portugal',
              'LI': 'Italy',
              'LO': 'Austria',
              'LS': 'Switzerland',
              'EK': 'Denmark',
              'ES': 'Sweden',
              'OM': 'United Arab Emirates',
              'OE': 'Saudi Arabia',
              'OH': 'Iraq',
              'OJ': 'Jordan',
              'OK': 'Kuwait',
              'OL': 'Lebanon',
              'LY': 'Serbia',
              'LT': 'Turkey',
              'LR': 'Romania',
              'LB': 'Bulgaria',
              'LH': 'Hungary',
              'LG': 'Greece',
              'LC': 'Cyprus',
              'LW': 'North Macedonia',
              'LA': 'Albania',
              'LQ': 'Bosnia and Herzegovina',
              'LD': 'Croatia',
              'LJ': 'Slovenia'
            };
            return icaoToCountryMap[prefix] || '';
          })();
          if (country) return `${code} (${country})`;
          return `${code} - ${found.name}`;
        }
      }
      // Fallback to returning the raw string
      return s;
    }

    const iata = airport.iata || airport.value || airport.code || '';
    const name = airport.name || airport.label || '';
    const city = airport.city || '';
    // Try to extract country from label like 'Name, Country' or from a country prop
    const country = airport.country || (() => {
      if (airport.label && airport.label.includes(', ')) return airport.label.split(', ').pop();
      return '';
    })();

    // For US airports, include state info
    if (country === 'United States' && city && usCityStates[city]) {
      const stateInfo = usCityStates[city];
      return `${iata} (${city}, ${stateInfo.code}, USA)`;
    }

    if (iata && country) return `${iata} (${country})`;
    if (iata && name) return `${iata} - ${name}`;
    if (name && country) return `${name} (${country})`;
    return iata || name || '';
  };

  // Ensure day 1 and last day contain only the outbound/return flight lines
  const enforceFlightOnlyDays = (route = [], homeAirport, arrivalAirport, returnAirport, durationDays) => {
    // Determine numeric day for each entry robustly (handles numbers, strings like 'Day 1', 0-based indices, or missing day)
    const extractDayNum = (r, idx) => {
      if (!r) return idx + 1;
      if (typeof r.day === 'number' && !Number.isNaN(r.day)) return Number(r.day);
      if (typeof r.day === 'string') {
        const m = r.day.match(/(\d+)/);
        if (m) return Number(m[1]);
      }
      return idx + 1; // fallback to position
    };

    const dayNums = (route || []).map((r, i) => extractDayNum(r, i));
    const inferredTotal = dayNums.length ? Math.max(...dayNums) : 0;
    const total = Number(durationDays) || inferredTotal || (route.length || 0);
    const homeLabel = formatAirportLabel(homeAirport) || '';
    const arrivalLabel = formatAirportLabel(arrivalAirport) || '';
    const returnLabel = formatAirportLabel(returnAirport) || arrivalLabel || '';

    const outbound = homeLabel && arrivalLabel ? `${homeLabel} -> ${arrivalLabel}` : (homeLabel || arrivalLabel || 'Outbound flight');
    const inbound = returnLabel && homeLabel ? `${returnLabel} -> ${homeLabel}` : (returnLabel || homeLabel || 'Return flight');

    // Work on a shallow copy and normalize day numbers into a numeric `dayNum` property
    let newRoute = (route || []).map((d, i) => ({ ...d, dayNum: extractDayNum(d, i) }));

    // Remove any non-day-1/last-day segments that look like flights (protect against AI placing flights on other days)
    const isFlightLabel = (s) => {
      if (!s) return false;
      return /flight|depart|departure|arriv|return/i.test(String(s));
    };

    newRoute = newRoute.filter(r => {
      const dayNum = Number(r.dayNum) || 0;
      if (dayNum !== 1 && total > 0 && dayNum !== total && isFlightLabel(r.city)) {
        // drop this segment (it was a misplaced flight label)
        return false;
      }
      return true;
    });

    // Ensure Day 1 exists and is the outbound flight
    // Compute min and max day numbers present (handle case where AI labels start from 0 or other offsets)
    const presentDays = newRoute.map(r => Number(r.dayNum) || 0).filter(n => n > 0);
    const minDay = presentDays.length ? Math.min(...presentDays) : 1;
    const maxDay = total > 0 ? total : (presentDays.length ? Math.max(...presentDays) : minDay);

    // Ensure minDay entry exists and is set to outbound
    const day1Index = newRoute.findIndex(r => Number(r.dayNum) === minDay);
    if (day1Index >= 0) {
      newRoute[day1Index].city = outbound;
      newRoute[day1Index].country = '';
      if (Array.isArray(newRoute[day1Index].activities)) newRoute[day1Index].activities = [];
      if (Array.isArray(newRoute[day1Index].suggestions)) newRoute[day1Index].suggestions = [];
      newRoute[day1Index].day = minDay; // normalize day field
      newRoute[day1Index].dayNum = minDay;
    } else {
      newRoute.push({ day: minDay, dayNum: minDay, city: outbound, country: '' });
    }

    // Ensure final day exists and is the inbound flight
    if (maxDay > 0) {
      const lastIndex = newRoute.findIndex(r => Number(r.dayNum) === maxDay || Number(r.day) === maxDay);
      if (lastIndex >= 0) {
        newRoute[lastIndex].city = inbound;
        newRoute[lastIndex].country = '';
        if (Array.isArray(newRoute[lastIndex].activities)) newRoute[lastIndex].activities = [];
        if (Array.isArray(newRoute[lastIndex].suggestions)) newRoute[lastIndex].suggestions = [];
        newRoute[lastIndex].day = maxDay;
        newRoute[lastIndex].dayNum = maxDay;
      } else {
        newRoute.push({ day: maxDay, dayNum: maxDay, city: inbound, country: '' });
      }
    }

    // Remove any remaining segments on day 1 or final day beyond the one we set
    const dedup = {};
    newRoute.forEach(r => {
      const d = Number(r.dayNum || r.day) || 0;
      // Keep only the last occurrence for each day
      dedup[d] = r;
    });

    // Ensure we keep all days between 1 and total (if total known) and sort
    const outDays = Object.keys(dedup).map(k => Number(k)).filter(n => n > 0);
    if (total > 0) {
      for (let i = 1; i <= total; i++) {
        if (!outDays.includes(i)) {
          // create empty placeholder for missing day (except between days where AI provided content)
          dedup[i] = { day: i, city: '', country: '' };
        }
      }
    }

    const out = Object.keys(dedup).map(k => dedup[k]).sort((a, b) => Number(a.day) - Number(b.day));
    return out;
  };

  // Generate city context for selected cities
  const generateCityContexts = useCallback(async (route) => {
    if (!route || route.length === 0) {
      console.log('[CityContext] No route provided');
      return;
    }
    
    try {
      // Collect unique cities from route
      const uniqueCities = [];
      const seenKeys = new Set();
      
      route.forEach(day => {
        if (day.city && day.country) {
          const key = `${day.city}, ${day.country}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            uniqueCities.push({ city: day.city, country: day.country });
          }
        }
      });
      
      console.log('[CityContext] Unique cities extracted:', uniqueCities);
      
      if (uniqueCities.length === 0) {
        console.log('[CityContext] No unique cities found');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('[CityContext] No authentication token found');
        return;
      }
      
      console.log('[CityContext] Calling backend API with cities:', uniqueCities);
      console.log('[CityContext] Travel pace:', form.travel_pace);
      console.log('[CityContext] Interests:', form.trip_interests);
      
      // Call API to generate city contexts
      const response = await fetch(`${API_PATH}/trips/generate_city_contexts.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cities: uniqueCities,
          travel_pace: form.travel_pace,
          trip_interests: form.trip_interests
        })
      });
      
      console.log('[CityContext] API Response Status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[CityContext] API Error:', response.status, errorText);
        return;
      }
      
      const data = await response.json();
      console.log('[CityContext] Received data:', data);
      
      if (data && typeof data === 'object' && !data.error) {
        setCityContexts(data);
        window._cityContexts = data;  // DEBUG: Expose to window for console access
        console.log('✅ City contexts successfully set in state:', Object.keys(data));
      } else if (data && data.error) {
        console.error('[CityContext] API returned error:', data.error);
      }
    } catch (error) {
      console.error('[CityContext] Exception occurred:', error.message, error);
    }
  }, [form.travel_pace, form.trip_interests]);

  // Handle AI preview generation
  const handlePreview = async (overrides = {}, retryCount = 0) => {
    setPreviewError(null);
    setIsPreviewing(true);
    setGlobalAiWorking(true);
    
    try {
      const token = localStorage.getItem('token');
      // Ensure all data is serializable (no React components or DOM elements)
      const safeSelectedCountries = (overrides.selected_countries_or_airports ?? (form.selectedCountriesOrAirports || [])).map(item => {
        if (typeof item === 'object' && item !== null) {
          return {
            value: item.value,
            label: item.label,
            type: item.type
          };
        }
        return item;
      });
      
      // Extract countries from selected destinations for major cities lookup
      const countriesInTrip = safeSelectedCountries.map(item => {
        if (item.type === 'country') {
          return item.label;
        }
        // For airports, try to extract country from the label (format: "City, Country")
        if (item.type === 'airport' && item.label.includes(', ')) {
          return item.label.split(', ').pop();
        }
        return null;
      }).filter(Boolean);

      // Build major cities guidance from the JSON data
      const summaryToUse = logisticSummary || logisticContextSummary;
      const logisticContextGuidance = summaryToUse
        ? `Logistical focus: ${summaryToUse}. Prioritize these answers when planning the route and city sequencing. `
        : '';

      const buildMajorCitiesGuidance = () => {
        const relevantCities = {};
        countriesInTrip.forEach(country => {
          if (majorCountryCities[country]) {
            relevantCities[country] = majorCountryCities[country];
          }
        });
        let guidance = `${logisticContextGuidance}CRITICAL: Use ONLY cities from the provided major_cities_data when planning day-by-day routes. `;
  guidance += "PRESERVE existing cities unless explicitly asked to remove them. Prefer allocating more days to explore an existing city rather than adding new cities. If only one day remains in the trip, do NOT add another city; instead allocate that day to the last destination. Provide a recommended number of days to explore each city based on trip style: Relaxed=4, Moderate=3, Fast=2. ";

        // Preference: prefer adding days to explore a city rather than adding more cities
        guidance += "PRIORITY: Prefer giving more days to existing cities to allow proper exploration rather than increasing the number of cities visited. " +
                    "When recommending days, include a short justification for the recommended duration. " +
                    "Recommended baseline days per major city by pace: Relaxed=4, Moderate=3, Fast=2. For smaller towns, 1-2 days may suffice. ";

        guidance += "When adding a new city, only add it if it significantly improves the itinerary; recommend the exact number of days needed to explore it and avoid creating zigzags. ";

        if (Object.keys(relevantCities).length > 0) {
          guidance += "For optimal travel flow, when selecting the next city in a country: ";
          guidance += "1) Choose from the major_cities_data list ONLY. ";
          guidance += "2) Pick the city geographically CLOSEST to your current location that you haven't visited yet. ";
          guidance += "3) Consider logical travel patterns (avoid unnecessary back-and-forth/zigzagging). ";
          guidance += "4) When possible, extend stay length in an existing city rather than inserting additional cities. ";

          Object.entries(relevantCities).forEach(([country, cities]) => {
            guidance += `${country}: [${cities.join(', ')}]. `;
          });
        } else {
          guidance += "Focus on major tourist cities and logical geographic flow. Use the baseline recommended days when suggesting durations.";
        }

        return guidance;
      };

      // Add strict guidance to prevent activities on day 1 and final day
      const flightGuidance = (() => {
        try {
          const homeLabel = formatAirportLabel(form.homeAirport);
          const arrivalLabel = formatAirportLabel(form.arrivalAirport);
          const returnLabel = formatAirportLabel(form.returnAirport) || arrivalLabel;
          return `\nIMPORTANT: DO NOT schedule any activities on Day 1 or on the FINAL day. Day 1 should ONLY show the outbound flight in this exact format: '${homeLabel} -> ${arrivalLabel}'. The final day should ONLY show the return flight in this exact format: '${returnLabel} -> ${homeLabel}'. Do NOT add sightseeing, tours, restaurants, transit or other activities on those days. Return the route only.`;
        } catch (e) {
          return "\nIMPORTANT: DO NOT schedule any activities on Day 1 or on the FINAL day. Day 1 should ONLY show the outbound flight 'HOME_IATA (Country) -> FIRST_DEST_IATA (Country)'. The final day should ONLY show the return flight 'LAST_DEST_IATA (Country) -> HOME_IATA (Country)'.";
        }
      })();

      const payload = {
        start_location: overrides.start_location ?? form.start_location,
        end_location: overrides.end_location ?? form.end_location,
        duration_days: String(overrides.duration_days ?? form.duration_days),
        name: String(overrides.name ?? (form.name || form.compiledName)),
        trip_style: String(overrides.trip_style ?? (form.travel_pace || 'Moderate')),
        preferred_activities: Array.isArray(overrides.preferred_activities ?? form.trip_interests) ? 
          (overrides.preferred_activities ?? form.trip_interests).filter(item => typeof item === 'string') : [],
        selected_countries_or_airports: safeSelectedCountries,
        major_cities_data: majorCountryCities, // Include the complete major cities database
        city_planning_guidance: buildMajorCitiesGuidance() + flightGuidance,
        logistic_context_summary: logisticContextSummary,
        // Policy flags to help the model follow constraints
        preserve_existing_cities: true,
        prefer_more_days_instead_of_adding_cities: true,
        recommended_days_per_style: { Relaxed: 4, Moderate: 3, Fast: 2 },
        retry_attempt: retryCount,
        // Include date information for fixed trips
        trip_type: overrides.trip_type ?? form.trip_type,
        start_date: overrides.start_date ?? form.start_date,
        end_date: overrides.end_date ?? form.end_date,
        // Provide a compact constraint field as well (server may use either)
        generation_constraints: flightGuidance,
        language: i18n.language
      };


      console.debug('TripWizard.handlePreview -> payload', payload);
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_PATH}/trips/preview_route.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      console.debug('TripWizard.handlePreview <- response', data);
      if (data && data.route) console.debug('TripWizard.handlePreview original route', JSON.parse(JSON.stringify(data.route)));
      
      // Handle 401 Unauthorized - user needs to log in
      if (res.status === 401) {
        setPreviewError(t('error_login_required', 'Please log in to build your trip. Your session may have expired.'));
        console.error('Preview failed: Unauthorized - user needs to log in');
        setIsPreviewing(false);
        setGlobalAiWorking(false);
        return;
      }
      
      if (res.ok && data.route) {
        // Post-process the returned route to guarantee flight-only Day 1 and final day
        const processedRoute = enforceFlightOnlyDays(data.route, form.homeAirport, (form.arrivalAirport || form.start_location), (form.returnAirport || form.end_location), payload.duration_days);
        console.debug('TripWizard.handlePreview processed route', JSON.parse(JSON.stringify(processedRoute)));
        // If an add-city flow is active, validate the AI response to ensure it only added the requested city
        if (addCityRequested) {
          try {
            const prevCities = new Set(safeCurrentPlan.map(d => d.city));
            const newCitiesSet = new Set(processedRoute.map(d => d.city));
            // Determine newly introduced cities
            const newlyAdded = [...newCitiesSet].filter(c => !prevCities.has(c));
            const lastDay = safeCurrentPlan.length > 0 ? safeCurrentPlan[safeCurrentPlan.length - 1].day : (data.route.length || 0);

            // Find requested city placement
            const placedSegments = processedRoute.filter(d => d.city === addCityRequested);
            const placedDays = placedSegments.map(d => d.day);
            const placedStart = placedDays.length > 0 ? Math.min(...placedDays) : null;
            const placedEnd = placedDays.length > 0 ? Math.max(...placedDays) : null;

            const onlyRequestedAdded = newlyAdded.length === 1 && newlyAdded[0] === addCityRequested;
            const placedBetween = placedStart && placedEnd && placedStart > 1 && placedEnd < lastDay;

            if (!onlyRequestedAdded || !placedBetween) {
              // Retry with a stricter instruction up to 2 retries
              if (addCityRetryCount < 2) {
                const stricter = `Place only ${addCityRequested} between day 2 and day ${Math.max(2, lastDay - 1)}. Do NOT add any other new cities. Keep all existing cities unless instructed otherwise. Avoid zigzagging: insert ${addCityRequested} where it produces the most logical geographic flow. Return the full route.`;
                setAddCityRetryCount(prev => prev + 1);
                console.warn('Add-city validation failed, retrying with stricter instruction:', { newlyAdded, placedStart, placedEnd, lastDay });
                // Trigger another regenerate with the stricter instruction
                handleRegenerate(0, stricter);
                return; // Don't apply the response yet
              } else {
                // Give up after retries and surface an error
                setPreviewError(`AI did not place ${addCityRequested} correctly after ${addCityRetryCount + 1} attempts. Please try again or edit the plan manually.`);
                console.error('Add-city failed after retries', { newlyAdded, placedStart, placedEnd, lastDay, route: data.route });
                // Clear add-city state
                setAddCityRequested(null);
                setAddCityRetryCount(0);
                return;
              }
            }
            // Passed validation - continue to apply the route
          } catch (valErr) {
            console.error('Error validating add-city response', valErr);
            // If validation fails unexpectedly, allow the route to be applied to avoid blocking; clear add-city state
            setAddCityRequested(null);
            setAddCityRetryCount(0);
          }
        }

        setPreviewRoute(processedRoute);
        setPreviewSummary(data.summary || '');
        
        // Generate city contexts for the selected cities
        generateCityContexts(processedRoute);
        
        const map = {};
        processedRoute.forEach(d => {
          map[`day-${d.day}`] = `${d.city}${d.country ? ', ' + d.country : ''}`;
        });
        setEditableDayLocations(map);
        
        // Extract unique countries from the AI-generated route and update trip name
        const routeCountries = [...new Set(processedRoute.map(d => d.country).filter(Boolean))];
        const newTripName = routeCountries.length > 0 ? `${routeCountries.join(', ')}` : '';
        setForm(prev => ({
          ...prev,
          compiledName: newTripName,
          name: (prev.name === prev.compiledName || !prev.name) ? newTripName : prev.name
        }));
        
        const snapshot = {
          route: processedRoute,
          editableDayLocations: map,
          summary: data.summary || '',
          params: {
            start_location: payload.start_location,
            end_location: payload.end_location,
            duration_days: payload.duration_days,
            trip_style: payload.trip_style,
            preferred_activities: payload.preferred_activities,
            selected_countries_or_airports: payload.selected_countries_or_airports,
            // Include date information for fixed trips
            trip_type: payload.trip_type,
            start_date: payload.start_date,
            end_date: payload.end_date
          }
        };
        
        if (historyIndex === -1) {
          setHistory([snapshot]);
          setHistoryIndex(0);
        } else {
          const base = history.slice(0, historyIndex + 1);
          base.push(snapshot);
          if (base.length > MAX_HISTORY) base.splice(0, base.length - MAX_HISTORY);
          setHistory(base);
          setHistoryIndex(base.length - 1);
        }
      } else {
        // Check if this is a parsing error and we can retry
        const isParsingError = data && data.error && (
          data.error.includes('Syntax error') || 
          data.error.includes('Failed to parse') ||
          data.message.includes('Failed to parse')
        );
        
        if (isParsingError && retryCount < 2) {
          setTimeout(() => handlePreview(overrides, retryCount + 1), 1000);
          return;
        }
        
        const msg = data && data.message ? 
          `${data.message}${data.error ? ': ' + data.error : ''}` : 
          'Initial AI generation failed';
        setPreviewError(msg + (retryCount > 0 ? ` (after ${retryCount + 1} attempts)` : ''));
        console.error('Preview failed after retries', data);
      }
    } catch (err) {
      // Network or other errors - also retry
      if (retryCount < 2) {
        setTimeout(() => handlePreview(overrides, retryCount + 1), 1000);
        return;
      }
      
      setPreviewError(err.message || `Initial AI generation failed (after ${retryCount + 1} attempts)`);
      console.error('Preview error after retries', err);
    } finally {
      setIsPreviewing(false);
      setGlobalAiWorking(false);
    }
  };

  // Handle AI regeneration with user instructions
  const handleRegenerate = async (retryCount = 0, instructions = null) => {
    setPreviewError(null);
    setIsPreviewing(true);
    setGlobalAiWorking(true);
    
    try {
      const token = localStorage.getItem('token');
      // Ensure all data is serializable (no React components or DOM elements)
      const safeSelectedCountries = (form.selectedCountriesOrAirports || []).map(item => {
        if (typeof item === 'object' && item !== null) {
          return {
            value: item.value,
            label: item.label,
            type: item.type
          };
        }
        return item;
      });
      
      const safeCurrentPlan = previewRoute ? previewRoute.map(day => {
        if (typeof day === 'object' && day !== null) {
          return {
            day: day.day,
            city: day.city,
            country: day.country
          };
        }
        return day;
      }) : [];
      
      const flightGuidance = (() => {
        try {
          const homeLabel = formatAirportLabel(form.homeAirport);
          const arrivalLabel = formatAirportLabel(form.arrivalAirport);
          const returnLabel = formatAirportLabel(form.returnAirport) || arrivalLabel;
          return `\nIMPORTANT: DO NOT schedule any activities on Day 1 or on the FINAL day. Day 1 should ONLY show the outbound flight in this exact format: '${homeLabel} -> ${arrivalLabel}'. The final day should ONLY show the return flight in this exact format: '${returnLabel} -> ${homeLabel}'. Do NOT add sightseeing, tours, restaurants, transit or other activities on those days. Return the route only.`;
        } catch (e) {
          return "\nIMPORTANT: DO NOT schedule any activities on Day 1 or on the FINAL day. Day 1 should ONLY show the outbound flight 'HOME_IATA (Country) -> FIRST_DEST_IATA (Country)'. The final day should ONLY show the return flight 'LAST_DEST_IATA (Country) -> HOME_IATA (Country)'.";
        }
      })();

      const regenPayload = {
        start_location: form.start_location,
        end_location: form.end_location,
        duration_days: String(form.duration_days || ''),
        name: String(form.name || form.compiledName || ''),
        trip_style: String(form.travel_pace || 'Moderate'),
        preferred_activities: Array.isArray(form.trip_interests) ? 
          form.trip_interests.filter(item => typeof item === 'string') : [],
        selected_countries_or_airports: safeSelectedCountries,
        current_plan: safeCurrentPlan,
        revision_instructions: String((instructions !== null && instructions !== undefined) ? instructions : (revisionInstructions || '')),
        // Policy flags for regeneration
        preserve_existing_cities: true,
        prefer_more_days_instead_of_adding_cities: true,
        recommended_days_per_style: { Relaxed: 4, Moderate: 3, Fast: 2 },
        // Ask the AI to apply the revision automatically and return the updated route
        agent_apply_revision: true,
        revision_apply_instructions: `Please apply the following revision to the current plan and return the updated route only (no extra commentary): ${(instructions !== null && instructions !== undefined) ? instructions : (revisionInstructions || '')}` + flightGuidance + `

IMPORTANT RULES FOR MODIFYING THE PLAN:
1. DO NOT add any new city to the existing cities in the plan UNLESS the user specifically asks to add a new city.
2. If you do add a city, consolidate all days spent in that city into ONE continuous sequence. For example: if the plan has X days in City A, then Y days in City B, and the user wants to add Z days to City A, the result should be (X+Z) days in City A followed by Y days in City B - NOT split visits to the same city.
3. User's explicit requests ALWAYS take priority over these rules.
4. When the user asks to ADD days to a city: take these days FROM future cities in the plan (reduce days from a future city). If a future city has only 1 day, remove that city entirely and use its day. If no future days are available, take from past cities.
5. When the user asks to REMOVE a day from a city: add this day to a future city (preferring the one with the fewest days). If no future cities exist, add to a past city.
6. The TOTAL number of days in the trip must NOT change unless the user explicitly asks to extend/shorten the trip duration. "Add one day to Paris" means reallocate a day TO Paris, not add a new day to the trip. Only "extend the trip by X days" should increase the total duration.
`,
        retry_attempt: Number(retryCount) || 0,
        // Include date information for fixed trips
        trip_type: form.trip_type,
        start_date: form.start_date,
        end_date: form.end_date,
        language: i18n.language
      };
      // Also attach a compact generation_constraints field for servers that read it
      regenPayload.generation_constraints = flightGuidance;
      
      // Debug: Check for any circular references before sending
      try {
        JSON.stringify(regenPayload);
      } catch (circularError) {
        console.error('Circular reference detected in payload:', circularError);
        console.error('Payload keys:', Object.keys(regenPayload));
        console.error('Form state:', {
          homeAirport: typeof form.homeAirport,
          returnAirport: typeof form.returnAirport,
          duration_days: typeof form.duration_days,
          name: typeof form.name,
          compiledName: typeof form.compiledName,
          travel_pace: typeof form.travel_pace,
          trip_interests: typeof form.trip_interests,
          selectedCountriesOrAirports: typeof form.selectedCountriesOrAirports
        });
        console.error('Other variables:', {
          revisionInstructions: typeof revisionInstructions,
          previewRoute: typeof previewRoute,
          retryCount: typeof retryCount
        });
        throw new Error('Cannot serialize payload: ' + circularError.message);
      }

      console.debug('TripWizard.handleRegenerate -> payload', regenPayload);
      const regenHeaders = { 'Content-Type': 'application/json' };
      if (token) {
        regenHeaders['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_PATH}/trips/preview_route.php`, {
        method: 'POST',
        headers: regenHeaders,
        body: JSON.stringify(regenPayload)
      });
      const data = await res.json();
      console.debug('TripWizard.handleRegenerate <- response', data);
      if (data && data.route) console.debug('TripWizard.handleRegenerate original route', JSON.parse(JSON.stringify(data.route)));
      
      // Handle 401 Unauthorized - user needs to log in
      if (res.status === 401) {
        setPreviewError(t('error_login_required', 'Please log in to build your trip. Your session may have expired.'));
        console.error('Regenerate failed: Unauthorized - user needs to log in');
        setIsPreviewing(false);
        setGlobalAiWorking(false);
        return;
      }
      
      if (res.ok && data.route) {
        // Post-process to ensure flight-only Day 1 and final day (use start/end fallbacks)
        const processedRoute = enforceFlightOnlyDays(data.route, form.homeAirport, (form.arrivalAirport || form.start_location), (form.returnAirport || form.end_location), regenPayload.duration_days);
        console.debug('TripWizard.handleRegenerate processed route', JSON.parse(JSON.stringify(processedRoute)));
        setPreviewRoute(processedRoute);
        setPreviewSummary(data.summary || '');
        
        // Generate city contexts for the selected cities
        generateCityContexts(processedRoute);
        
        const map = {};
        processedRoute.forEach(d => {
          map[`day-${d.day}`] = `${d.city}${d.country ? ', ' + d.country : ''}`;
        });
        setEditableDayLocations(map);
        
        // Extract unique countries from the AI-generated route and update trip name
        const routeCountries = [...new Set(processedRoute.map(d => d.country).filter(Boolean))];
        const newTripName = routeCountries.length > 0 ? `${routeCountries.join(', ')}` : '';
        setForm(prev => ({
          ...prev,
          compiledName: newTripName,
          name: (prev.name === prev.compiledName || !prev.name) ? newTripName : prev.name
        }));
        
        const snapshot = {
          route: processedRoute,
          editableDayLocations: map,
          summary: data.summary || '',
          params: {
            start_location: regenPayload.start_location,
            end_location: regenPayload.end_location,
            duration_days: regenPayload.duration_days,
            trip_style: regenPayload.trip_style,
            preferred_activities: regenPayload.preferred_activities,
            selected_countries_or_airports: regenPayload.selected_countries_or_airports,
            revision_instructions: regenPayload.revision_instructions,
            // Preserve date information for fixed trips
            trip_type: regenPayload.trip_type,
            start_date: regenPayload.start_date,
            end_date: regenPayload.end_date
          }
        };
        
        const base = history.slice(0, historyIndex + 1);
        base.push(snapshot);
        if (base.length > MAX_HISTORY) base.splice(0, base.length - MAX_HISTORY);
        setHistory(base);
        setHistoryIndex(base.length - 1);
        
        // Add to revision history chat for display
        if (revisionInstructions.trim()) {
          setRevisionHistory(prev => [...prev, 
            { sender: 'user', text: revisionInstructions },
            { sender: 'agent', text: 'Route updated successfully.' }
          ]);
        }
        
        setRevisionInstructions('');
        // Clear add-city state if it was set and we successfully applied the route
        if (addCityRequested) {
          setAddCityRequested(null);
          setAddCityRetryCount(0);
        }
      } else {
        // Check if this is a parsing error and we can retry
        const isParsingError = data && data.error && (
          data.error.includes('Syntax error') || 
          data.error.includes('Failed to parse') ||
          data.message.includes('Failed to parse')
        );
        
        if (isParsingError && retryCount < 2) {
          // Wait a moment before retry
          setTimeout(() => handleRegenerate(retryCount + 1), 1000);
          return;
        }
        
        const msg = data && data.message ? 
          `${data.message}${data.error ? ': ' + data.error : ''}` : 
          'AI regeneration failed';
        setPreviewError(msg + (retryCount > 0 ? ` (after ${retryCount + 1} attempts)` : ''));
        console.error('Preview failed after retries', data);
      }
    } catch (err) {
      // Network or other errors - also retry
      if (retryCount < 2) {
        setTimeout(() => handleRegenerate(retryCount + 1), 1000);
        return;
      }
      
      setPreviewError(err.message || `AI regeneration failed (after ${retryCount + 1} attempts)`);
      console.error('Preview error after retries', err);
    } finally {
      setIsPreviewing(false);
      setGlobalAiWorking(false);
    }
  };

  // --- Step 5: itinerary summary helpers & actions ---
  const [addCityOpen, setAddCityOpen] = useState(false);
  const [addCityCandidates, setAddCityCandidates] = useState([]);
  const [addCityRequested, setAddCityRequested] = useState(null);
  const [addCityRetryCount, setAddCityRetryCount] = useState(0);

  const summarizeRoute = (route) => {
    if (!route || route.length === 0) return [];
    const segments = [];
    // Group consecutive days by city
    let i = 0;
    while (i < route.length) {
      const start = i;
      const city = route[i].city;
      const country = route[i].country;
      let j = i + 1;
      while (j < route.length && route[j].city === city && route[j].country === country) j++;
      const days = j - start;
      segments.push({ type: 'city', city, country, startDay: start + 1, endDay: j, days });
      i = j;
    }
    return segments;
  };

  const formatAirportForMap = (airport) => {
    if (!airport) return null;
    if (airport.city && airport.country) return `${airport.city}, ${airport.country}`;
    if (airport.city) return airport.city;
    if (airport.name) return airport.name;
    if (airport.value) return airport.value;
    if (airport.label) return airport.label;
    if (airport.iata) return airport.iata;
    return null;
  };

  const routeMapCities = useMemo(() => {
    if (!previewRoute || previewRoute.length === 0) return [];
    const labels = [];
    const pushUnique = (label) => {
      if (!label) return;
      if (labels.length === 0 || labels[labels.length - 1] !== label) {
        labels.push(label);
      }
    };

    const dayNumbers = previewRoute
      .map(entry => Number(entry.day || entry.dayNum || entry.dayNumber || 0))
      .filter(n => n > 0);
    if (dayNumbers.length === 0) return [];
    const minDay = Math.min(...dayNumbers);
    const maxDay = Math.max(...dayNumbers);

    previewRoute.forEach(day => {
      const dayValue = Number(day.day || day.dayNum || day.dayNumber || 0);
      if (!dayValue) return;
      if (dayValue <= minDay || dayValue >= maxDay) return;
      const cityName = day.city;
      const countryName = day.country;
      if (!cityName) return;
      const formatted = countryName ? `${cityName}, ${countryName}` : cityName;
      pushUnique(formatted);
    });
    return labels;
  }, [previewRoute]);

  const openAddCityDialog = () => {
    if (!previewRoute) return;
    // Collect countries present in trip
    const countries = [...new Set(previewRoute.map(d => d.country).filter(Boolean))];
    // Build candidate cities from majorCountryCities for those countries
    const candidates = [];
    countries.forEach(country => {
      const list = majorCountryCities[country];
      if (Array.isArray(list)) {
        list.forEach(city => candidates.push({ city, country }));
      }
    });
    // Remove cities already on the plan
    const existing = new Set(previewRoute.map(d => d.city));
    const filtered = candidates.filter(c => !existing.has(c.city));
    setAddCityCandidates(filtered.slice(0, 50));
    setAddCityOpen(true);
  };

  const handleAddCity = (cityObj) => {
    // User requested add city -> set revisionInstructions and regenerate
    const cityName = cityObj.city;
  const instr = `Add only ${cityName} to the plan with minimum 2 days in that city. Do NOT add any other new cities. Prefer increasing days in existing cities rather than adding more cities. If there is only one spare day left in the trip, do NOT add ${cityName}; instead allocate that spare day to the last destination. Recommend how many days are needed to properly explore ${cityName} and give a short justification. Place ${cityName} in the best logical spot between day 2 and the last day and adjust durations by redistributing days from other cities as needed. Avoid creating zigzags.`;
    setRevisionInstructions(instr);
    setAddCityOpen(false);
    setAddCityRequested(cityName);
    setAddCityRetryCount(0);
    // trigger regen immediately with the instruction to avoid state race
    handleRegenerate(0, instr);
  };

  const handleCityReorder = (sourceIndex, targetIndex) => {
    if (sourceIndex === targetIndex) return;
    
    const segments = summarizeRoute(previewRoute);
    // Attach original items to each segment
    const segmentsWithItems = segments.map(seg => ({
      ...seg,
      items: previewRoute.slice(seg.startDay - 1, seg.endDay)
    }));

    // Reorder
    const [movedSegment] = segmentsWithItems.splice(sourceIndex, 1);
    segmentsWithItems.splice(targetIndex, 0, movedSegment);

    // Rebuild route
    const newRoute = [];
    let currentDay = 1;
    
    segmentsWithItems.forEach(seg => {
      seg.items.forEach(item => {
        newRoute.push({
          ...item,
          day: currentDay
        });
        currentDay++;
      });
    });

    setPreviewRoute(newRoute);
    
    // Update editableDayLocations map
    const map = {};
    newRoute.forEach(d => {
      map[`day-${d.day}`] = `${d.city}${d.country ? ', ' + d.country : ''}`;
    });
    setEditableDayLocations(map);
    
    // Update history
    const snapshot = {
      route: newRoute,
      editableDayLocations: map,
      summary: previewSummary,
      params: history[historyIndex]?.params || {}
    };
    
    const base = history.slice(0, historyIndex + 1);
    base.push(snapshot);
    if (base.length > MAX_HISTORY) base.splice(0, base.length - MAX_HISTORY);
    setHistory(base);
    setHistoryIndex(base.length - 1);
  };

  const handleRemoveCity = (cityName) => {
    if (!cityName) return;
    if (!confirm(t('confirm_remove_city', { cityName }))) return;
    const instr = `remove ${cityName} from the plan. do not add additional new cities. use the days from that city and spread it to the remaining of the trip`;
    setRevisionInstructions(instr);
    handleRegenerate(0, instr);
  };

  const handleChangeCityDays = (cityName, days) => {
    if (!cityName) return;
    if (days < 1) return;
    const instr = `in ${cityName} spend ${days} days only`;
    setRevisionInstructions(instr);
    handleRegenerate(0, instr);
  };

  // Apply snapshot from history
  const applySnapshot = (snap) => {
    if (!snap) return;
    setPreviewRoute(snap.route || null);
    setEditableDayLocations(snap.editableDayLocations || {});
    setPreviewSummary(snap.summary || '');
  };

  // Handle day location changes
  const handleDayLocationChange = (dayKey, value) => {
    setEditableDayLocations(prev => ({ ...prev, [dayKey]: value }));
  };

  // Handle undo functionality
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const snapshot = history[newIndex];
      applySnapshot(snapshot);
      setHistoryIndex(newIndex);
    }
  };

  // Handle redo functionality  
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const snapshot = history[newIndex];
      applySnapshot(snapshot);
      setHistoryIndex(newIndex);
    }
  };

  // Handle form submission to backend
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setIsSubmitting(true);
    setGlobalAiWorking(true);
    
    // Always update user profile preference on submit
    localStorage.setItem('user_trip_preferences', JSON.stringify({
      travel_pace: form.travel_pace,
      trip_interests: form.trip_interests
    }));
    
    // Convert confirmed blocks to day-by-day route format
    // This is the user's confirmed plan from InteractiveRouteBuilder
    const convertBlocksToRoute = (blocks) => {
      if (!blocks || !Array.isArray(blocks) || blocks.length === 0) return null;
      const route = [];
      blocks.forEach(block => {
        for (let day = block.start_day; day <= block.end_day; day++) {
          route.push({
            day: day,
            city: block.city,
            country: block.country,
            iata: block.iata || '',
            explanation: block.explanation || ''
          });
        }
      });
      return route;
    };
    
    // Read confirmed blocks from ref (synchronous) - state may be stale if called right after setState
    const latestConfirmedBlocks = confirmedBlocksFromBuilderRef.current || confirmedBlocksFromBuilder;
    
    // Use confirmed blocks if available, otherwise fall back to previewRoute
    const finalRoute = latestConfirmedBlocks 
      ? convertBlocksToRoute(latestConfirmedBlocks) 
      : previewRoute;
    
    // Build initial_day_locations from confirmed blocks so backend marks route_finalized=1
    // This prevents generate_itinerary.php from regenerating the route in Phase 1
    const buildInitialDayLocations = (blocks) => {
      if (!blocks || !Array.isArray(blocks) || blocks.length === 0) return null;
      const locs = {};
      blocks.forEach(block => {
        for (let day = block.start_day; day <= block.end_day; day++) {
          locs[`day-${day}`] = block.country 
            ? `${block.city}, ${block.country}` 
            : block.city;
        }
      });
      return locs;
    };
    
    const initialDayLocationsFromBlocks = latestConfirmedBlocks 
      ? buildInitialDayLocations(latestConfirmedBlocks) 
      : null;
    
    console.log('[TripWizard] Using route:', latestConfirmedBlocks ? 'confirmed blocks' : 'preview route', finalRoute);
    
    // Ensure all data is serializable (no React components or DOM elements)
    const safeSelectedCountries = (form.selectedCountriesOrAirports || []).map(item => {
      if (typeof item === 'object' && item !== null) {
        return {
          value: item.value,
          label: item.label,
          type: item.type
        };
      }
      return item;
    });
    
    // Build trip payload
    const tripPayload = {
      name: String(form.name || form.compiledName),
      start_location: form.start_location,
      end_location: form.end_location,
      home_airport: form.homeAirport ? `${form.homeAirport.name} (${form.homeAirport.iata})` : null,
      trip_type: String(form.trip_type),
      travel_pace: String(form.travel_pace),
      trip_interests: Array.isArray(form.trip_interests) ? 
        form.trip_interests.filter(item => typeof item === 'string') : [],
      selected_countries_or_airports: safeSelectedCountries,
      logistic_summary: logisticSummary || logisticContextSummary || '',
      // Use the user-confirmed route from InteractiveRouteBuilder, or fall back to preview
      route: finalRoute || null,
      // Add trip type specific fields
      ...(form.trip_type === 'fixed'
        ? {
            start_date: form.start_date,
            end_date: form.end_date,
            duration_days: form.duration_days
          }
        : {
            month_of_year: form.start_month ? parseInt(form.start_month.split('-')[1]) : null,
            flexible_year: form.start_month ? parseInt(form.start_month.split('-')[0]) : null,
            duration_days: form.duration_days
          })
    };

    if (form.trip_type !== 'fixed' && form.start_month) {
    }

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add day locations if available
    if (editableDayLocations && Object.keys(editableDayLocations).length > 0) {
      const mapped = {};
      if (form.trip_type === 'fixed' && form.start_date) {
        // Map day-N to actual dates for fixed trips
        const start = new Date(form.start_date);
        Object.keys(editableDayLocations).forEach(k => {
          const m = k.match(/^day-(\d+)$/i);
          if (m) {
            const dayIndex = parseInt(m[1], 10) - 1;
            const d = new Date(start);
            d.setDate(start.getDate() + dayIndex);
            const iso = d.toISOString().slice(0, 10);
            mapped[iso] = editableDayLocations[k];
          } else {
            mapped[k] = editableDayLocations[k];
          }
        });
      } else if (form.trip_type !== 'fixed' && form.start_month && form.duration_days) {
        // Map day-N to dates starting at first of month for flexible trips
        const parts = (form.start_month || '').split('-');
        const year = parseInt(parts[0], 10) || new Date().getFullYear();
        const month = parseInt(parts[1], 10) || 1;
        const base = new Date(year, month - 1, 1);
        Object.keys(editableDayLocations).forEach(k => {
          const m = k.match(/^day-(\d+)$/i);
          if (m) {
            const dayIndex = parseInt(m[1], 10) - 1;
            const d = new Date(base);
            d.setDate(base.getDate() + dayIndex);
            const iso = d.toISOString().slice(0, 10);
            mapped[iso] = editableDayLocations[k];
          } else {
            mapped[k] = editableDayLocations[k];
          }
        });
      } else {
        // No deterministic mapping possible
        Object.assign(mapped, editableDayLocations);
      }
      tripPayload.initial_day_locations = mapped;
    }
    
    // Confirmed blocks always override editableDayLocations - prevents generate_itinerary.php from regenerating
    if (initialDayLocationsFromBlocks) {
      tripPayload.initial_day_locations = initialDayLocationsFromBlocks;
    }

    try {
      // Choose API endpoint based on mode
      const apiUrl = actualMode === 'update' 
        ? `${API_PATH}/trips/update.php?trip_id=${initialData?.trip_id}`
        : `${API_PATH}/trips/create.php`;
      
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(tripPayload)
      });
      
      const data = await res.json();
      
      if (actualMode === 'update') {
        // Handle update success
        if (res.ok) {
          setForm(prev => ({ ...prev, feasibilityWarning: '' }));
          setGlobalAiWorking(false);
          
          // Show success message and call onSubmit callback if provided
          alert(t('trip_updated_success'));
          if (onSubmit) {
            onSubmit(data);
          }
        } else {
          throw new Error(data.message || t('trip_update_failed'));
        }
      } else {
        // Handle create success
        const tripId = data && (data.trip_id || (data.trip && data.trip.trip_id) || data.id || data.tripId);
        if (tripId) {
          // Clear any previous warnings
          setForm(prev => ({ ...prev, feasibilityWarning: '' }));
          setGlobalAiWorking(false);
          
          // Navigate directly to the itinerary page
          window.location.href = `/travel2/trip/${tripId}`;
        } else {
          throw new Error(data.message || t('trip_create_failed'));
        }
      }
    } catch (err) {
      console.error(t('trip_creation_failed_log'), err);
      setForm(prev => ({ ...prev, feasibilityWarning: err.message || t('trip_create_failed') }));
      setGlobalAiWorking(false);
    }
  };

  // Automatically trigger preview when entering Step 5
  useEffect(() => {
    const previewStep = mode === 'create' ? 6 : 3;
    if (step === previewStep && !isPreviewing) {
      // Calculate duration based on trip type
      let duration = 0;
      if (form.trip_type === 'fixed' && form.start_date && form.end_date) {
        try {
          const sd = new Date(form.start_date);
          const ed = new Date(form.end_date);
          const diff = Math.round((ed - sd) / (1000 * 60 * 60 * 24));
          duration = diff + 1; // inclusive
        } catch (e) {
          duration = 0;
        }
      } else {
        duration = parseInt(form.duration_days || 0, 10);
      }

      // Only call preview if we have required inputs
      const startLocation = form.homeAirport ? `${form.homeAirport.name} (${form.homeAirport.iata})` : '';
      if (startLocation && duration > 0) {
        const overrides = {
          duration_days: String(duration),
          selected_countries_or_airports: form.selectedCountriesOrAirports || []
        };
        setForm(prev => ({ ...prev, duration_days: String(duration) }));
        handlePreview(overrides);
      } else {
        setPreviewError(t('wizard_validation.home_airport_duration_required'));
      }
    }
  }, [step, actualMode]);

  const confirmAndSubmit = async () => {
    // Close dialog then submit; handleSubmit manages submitting state
    setConfirmOpen(false);
    await handleSubmit();
  };
  // Note: Early trip creation removed - InteractiveRouteBuilder works in preview mode (tripData.id = null)
  // Trip is created once in handleSubmit when user confirms, with the finalized confirmed blocks.

  // Now render the form
  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-stretch tw-justify-center tw-bg-black/60 tw-backdrop-blur-sm tw-overflow-hidden tw-px-4 tw-py-4">
      <div className="tw-relative tw-w-full tw-max-w-2xl tw-rounded-3xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-shadow-2xl tw-select-none tw-flex tw-flex-col tw-overflow-hidden">
        <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden">
      {/* CREATE MODE - Step 1: Airport Selection (NEW FIRST STEP) */}
      {actualMode === 'create' && step === 1 && (
        <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
          <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
          {renderStepHeader(t('wizard_step1_title'))}
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('wizard_step1_desc')}
          </Typography>

          {/* Visual Flight Path Builder */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#161b22', borderRadius: 2, minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(99,102,241,0.2)' }}>
            
            {/* Stage 1: No airports selected yet */}
            {!form.homeAirport && (
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#e6edf3' }}>
                  🏠 {t('wizard_home_airport_title')}
                </Typography>
                
                {/* Quick selection button for previously used home airport */}
                {savedHomeAirport && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: '#8b949e' }}>
                      {t('wizard_prev_home_airport')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSelectSavedHomeAirport}
                      sx={{ mb: 2, bgcolor: 'rgb(50,50,180)', '&:hover': { bgcolor: 'rgb(40,40,160)' }, color: 'white', border: '1px solid rgba(150,150,220,0.4)' }}
                    >
                      {savedHomeAirport.label} ({savedHomeAirport.value})
                    </Button>
                    <Typography variant="body2" sx={{ color: '#8b949e' }}>
                      {t('wizard_search_diff_airport')}
                    </Typography>
                  </Box>
                )}
                
                <TextField
                  label={t('wizard_search_home_airport')}
                  value={homeAirportInput}
                  onChange={handleHomeAirportInput}
                  placeholder={t('wizard_airport_placeholder')}
                  fullWidth
                  margin="normal"
                  sx={{ maxWidth: '400px' }}
                />
                
                {homeAirportOptions.length > 0 && (
                  <Paper sx={{ maxHeight: 180, overflowY: 'auto', mt: 1, maxWidth: '400px', mx: 'auto', bgcolor: '#21262d', border: '1px solid rgba(99,102,241,0.25)' }}>
                    <List>
                      {homeAirportOptions.map((airport, idx) => (
                        <ListItem button key={`home-${airport.value}-${idx}`} onClick={() => handleSelectHomeAirport(airport)}>
                          <ListItemText 
                            primary={airport.label}
                            secondary={t('wizard_home_airport_label')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            )}

            {/* Stage 2: Home selected, show destination selection */}
            {form.homeAirport && !form.arrivalAirport && (
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 1, color: '#8b949e' }}>
                  {t('wizard_flying_out_from')} <strong>{form.homeAirport.label}</strong>.
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, color: '#e6edf3' }}>
                  ✈️ {t('wizard_destination_prompt')}
                </Typography>
                <TextField
                  label={t('wizard_search_dest_airport')}
                  value={arrivalAirportInput}
                  onChange={handleArrivalAirportInput}
                  placeholder={t('wizard_airport_placeholder')}
                  fullWidth
                  margin="normal"
                  sx={{ maxWidth: '400px' }}
                />
                
                {arrivalAirportOptions.length > 0 && (
                  <Paper sx={{ maxHeight: 180, overflowY: 'auto', mt: 1, maxWidth: '400px', mx: 'auto', bgcolor: '#21262d', border: '1px solid rgba(99,102,241,0.25)' }}>
                    <List>
                      {arrivalAirportOptions.map((airport, idx) => (
                        <ListItem button key={`arrival-${airport.value}-${idx}`} onClick={() => handleSelectArrivalAirport(airport)}>
                          <ListItemText 
                            primary={airport.label}
                            secondary={t('wizard_dest_airport_label')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            )}



            {/* Stage 3: Both home and arrival selected - show trip type selection and flight path */}
            {form.homeAirport && form.arrivalAirport && (
              <Box sx={{ width: '100%' }}>
                {/* Trip Type Selection */}
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#e6edf3' }}>
                    {t('wizard_trip_type_prompt')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', maxWidth: '400px', mx: 'auto' }}>
                    <Button
                      variant={form.isMultiCity ? 'outlined' : 'contained'}
                      onClick={() => setForm(prev => ({ 
                        ...prev, 
                        isMultiCity: false,
                        returnAirport: prev.arrivalAirport // Auto-set return same as arrival for round trip
                      }))}
                      sx={{ 
                        flex: 1,
                        color: form.isMultiCity ? '#8b949e' : 'white',
                        bgcolor: form.isMultiCity ? 'transparent' : '#6366f1',
                        borderColor: '#6366f1',
                        '&:hover': {
                          bgcolor: form.isMultiCity ? 'rgba(99,102,241,0.1)' : '#4f46e5',
                          borderColor: '#6366f1'
                        }
                      }}
                    >
                      {t('wizard_trip_type_round')}
                    </Button>
                    <Button
                      variant={form.isMultiCity ? 'contained' : 'outlined'}
                      onClick={() => setForm(prev => ({ 
                        ...prev, 
                        isMultiCity: true,
                        returnAirport: null // Clear return airport for multi-city
                      }))}
                      sx={{ 
                        flex: 1,
                        color: form.isMultiCity ? 'white' : '#8b949e',
                        bgcolor: form.isMultiCity ? '#6366f1' : 'transparent',
                        borderColor: '#6366f1',
                        '&:hover': {
                          bgcolor: form.isMultiCity ? '#4f46e5' : 'rgba(99,102,241,0.1)',
                          borderColor: '#6366f1'
                        }
                      }}
                    >
                      {t('wizard_trip_type_multi')}
                    </Button>
                  </Box>
                </Box>

                {/* Simple Flight Path Summary */}
                <Box sx={{ 
                  mt: 2, 
                  p: { xs: 2, sm: 3 }, 
                  bgcolor: '#21262d', 
                  borderRadius: 1, 
                  border: '2px solid rgba(99,102,241,0.3)' 
                }}>
                  <Typography variant={{ xs: 'subtitle1', sm: 'h6' }} sx={{ mb: 2, textAlign: 'center' }}>
                    ✈️ {t('wizard_flight_journey_title')}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    flexWrap: 'wrap', 
                    gap: { xs: 0.5, sm: 1 }
                  }}>
                    <Typography variant={{ xs: 'body2', sm: 'body1' }} sx={{ fontWeight: 'bold', color: 'rgb(150,150,220)' }}>
                      {form.homeAirport?.iata || 'HOME'}
                    </Typography>
                    <Typography variant={{ xs: 'body2', sm: 'body1' }}>→</Typography>
                    <Typography variant={{ xs: 'body2', sm: 'body1' }} sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {form.arrivalAirport?.iata || form.arrivalAirport?.value}
                    </Typography>
                    {form.isMultiCity && form.returnAirport ? (
                      <>
                        <Typography variant={{ xs: 'body2', sm: 'body1' }}>→ ... →</Typography>
                        <Typography variant={{ xs: 'body2', sm: 'body1' }} sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                          {form.returnAirport.iata}
                        </Typography>
                        <Typography variant={{ xs: 'body2', sm: 'body1' }}>→</Typography>
                        <Typography variant={{ xs: 'body2', sm: 'body1' }} sx={{ fontWeight: 'bold', color: 'rgb(150,150,220)' }}>
                          {form.homeAirport?.iata || 'HOME'}
                        </Typography>
                      </>
                    ) : !form.isMultiCity ? (
                      <>
                        <Typography variant={{ xs: 'body2', sm: 'body1' }}>→</Typography>
                        <Typography variant={{ xs: 'body2', sm: 'body1' }} sx={{ fontWeight: 'bold', color: 'rgb(150,150,220)' }}>
                          {form.homeAirport?.iata || 'HOME'}
                        </Typography>
                      </>
                    ) : null}
                  </Box>
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary', 
                    textAlign: 'center', 
                    display: 'block', 
                    mt: 1,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }}>
                    {t('wizard_flight_journey_desc')}
                  </Typography>
                </Box>

                {/* Multi-City Return Airport Selection */}
                {form.isMultiCity && !form.returnAirport && (
                  <Box sx={{ 
                    mt: 3,
                    textAlign: 'center', 
                    p: { xs: 2, sm: 3 }, 
                    bgcolor: '#161b22', 
                    borderRadius: 2, 
                    border: '2px dashed rgba(99,102,241,0.4)'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#e6edf3' }}>
                      {t('wizard_return_airport_title')}
                    </Typography>
                    <TextField
                      label={t('wizard_search_return_airport')}
                      value={returnAirportInput}
                      onChange={handleReturnAirportInput}
                      onFocus={handleReturnAirportFocus}
                      placeholder={t('wizard_airport_placeholder')}
                      sx={{ width: { xs: '100%', sm: '300px' } }}
                    />
                    
                    {returnAirportOptions.length > 0 && returnDropdownOpen && (
                      <Paper sx={{ 
                        maxHeight: 200, 
                        overflowY: 'auto', 
                        mt: 1, 
                        position: 'absolute', 
                        zIndex: 1000, 
                        width: { xs: '90%', sm: '300px' },
                        bgcolor: '#21262d',
                        border: '1px solid rgba(99,102,241,0.25)'
                      }}>
                        <List>
                          {returnAirportOptions.map((airport, idx) => (
                            <ListItem 
                              button 
                              key={`return-${airport.value}-${idx}`} 
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleSelectReturnAirport(airport)}
                            >
                              <ListItemText 
                                primary={airport.label}
                                secondary={t('wizard_return_airport_label')}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          </div>
          <div className="tw-flex tw-justify-end tw-px-6 tw-py-4 tw-border-t tw-border-surface-border tw-flex-shrink-0">
            <button
              type="button"
              onClick={nextStep}
              disabled={!form.homeAirport || !form.arrivalAirport}
              className="tw-px-6 tw-py-2.5 tw-rounded-xl tw-bg-accent tw-text-white tw-font-semibold tw-text-sm tw-transition-all tw-duration-200 tw-hover:tw-brightness-110 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
            >
              {t('wizard_next')}
            </button>
          </div>
        </div>
      )}

      {/* CREATE MODE - Step 2: Country Selection (AUTO-DERIVED + ADDITIONAL) */}
      {actualMode === 'create' && step === 2 && (
        <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
          <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
          {renderStepHeader(t('wizard_step2_title'))}
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('wizard_step2_desc')}
          </Typography>

          {/* Show All Selected Countries */}
          {(() => {
            const allCountries = form.selectedCountriesOrAirports.filter(item => item.type === 'country');
            return allCountries.length > 0 && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgb(50,50,180)', borderRadius: 1, border: '1px solid rgba(150,150,220,0.35)' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#e6edf3' }}>
                  ✈️ {t('wizard_countries_visit')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {allCountries
                    .sort((a, b) => (a.order || 50) - (b.order || 50))
                    .map((country, idx) => (
                    <Chip
                      key={`country-${country.value}-${idx}`}
                      label={country.label}
                      color="success"
                      variant="filled"
                      icon={<Typography sx={{ fontSize: '14px' }}>🌍</Typography>}
                    />
                  ))}
                </Box>
              </Box>
            );
          })()}

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            {t('wizard_add_more_countries')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {t('wizard_add_more_countries_desc')}
          </Typography>
          
          <TextField
            label={t('wizard_add_more_placeholder')}
            value={countryAirportInput}
            onChange={handleCountryAirportInput}
            placeholder={t('wizard_type_destinations')}
            fullWidth
            margin="normal"
          />
          
          {countryAirportOptions.length > 0 && (
            <Paper sx={{ maxHeight: 180, overflowY: 'auto', mt: 1, bgcolor: '#21262d', border: '1px solid rgba(99,102,241,0.25)' }}>
              <List>
                {countryAirportOptions.map((opt, idx) => (
                  <ListItem button key={`${opt.type}-${opt.value}-${idx}`} onClick={() => handleSelectCountryAirport(opt)}>
                    <ListItemText 
                      primary={opt.label}
                      secondary={opt.type === 'city' ? t('wizard_city') : opt.type === 'country' ? t('wizard_country') : t('wizard_airport')}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
          
          </div>
          <div className="tw-flex tw-justify-between tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-surface-border tw-flex-shrink-0">
            <button
              type="button"
              onClick={prevStep}
              className="tw-px-5 tw-py-2.5 tw-rounded-xl tw-border tw-border-surface-border tw-text-sm tw-text-muted tw-transition-all tw-duration-200 tw-hover:tw-text-muted-fore tw-hover:tw-bg-surface-3"
            >
              {t('wizard_back')}
            </button>
            <button
              type="button"
              onClick={nextStep}
              className="tw-px-6 tw-py-2.5 tw-rounded-xl tw-bg-accent tw-text-white tw-font-semibold tw-text-sm tw-transition-all tw-duration-200 tw-hover:tw-brightness-110"
            >
              {t('wizard_next')}
            </button>
          </div>
        </div>
      )}



      {/* CREATE MODE - Step 4: Duration */}
      {actualMode === 'create' && step === 4 && (
        <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
          <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
          {renderStepHeader(t('wizard_step4_title'))}
          
          {/* Show All Selected Countries */}
          {(() => {
            const allCountries = form.selectedCountriesOrAirports.filter(item => item.type === 'country');
            return allCountries.length > 0 && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgb(50,50,180)', borderRadius: 1, border: '1px solid rgba(150,150,220,0.35)' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#e6edf3' }}>
                  ✈️ {t('wizard_countries_visit')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {allCountries
                    .sort((a, b) => (a.order || 50) - (b.order || 50))
                    .map((country, idx) => (
                    <Chip
                      key={`country-${country.value}-${idx}`}
                      label={country.label}
                      color="success"
                      variant="filled"
                      icon={<Typography sx={{ fontSize: '14px' }}>🌍</Typography>}
                    />
                  ))}
                </Box>
              </Box>
            );
          })()}
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('wizard_trip_type_label')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="radio" 
                  name="trip_type" 
                  value="fixed" 
                  checked={form.trip_type === 'fixed'} 
                  onChange={() => {
                    setForm(prev => ({ 
                      ...prev, 
                      trip_type: 'fixed',
                      duration_days: '', // Clear flexible duration
                      start_month: ''    // Clear flexible month
                    }));
                    // Don't clear date range as user might want to keep their selections
                  }} 
                  style={{ marginRight: '8px' }}
                />
                {t('wizard_fixed_dates')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="radio" 
                  name="trip_type" 
                  value="flexible" 
                  checked={form.trip_type === 'flexible'} 
                  onChange={() => {
                    setForm(prev => ({ 
                      ...prev, 
                      trip_type: 'flexible',
                      start_date: '',    // Clear fixed dates
                      end_date: ''       // Clear fixed dates
                    }));
                  }} 
                  style={{ marginRight: '8px' }}
                />
                {t('wizard_flexible_duration')}
              </label>
            </Box>
          </Box>
          
          {form.trip_type === 'fixed' ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('wizard_select_dates')}</Typography>
                <DateRangeInput 
                  startDate={form.start_date}
                  endDate={form.end_date}
                  onChange={(startDate, endDate) => {
                    setForm(prev => ({
                      ...prev,
                      start_date: startDate,
                      end_date: endDate,
                      duration_days: startDate && endDate ? dayjs(endDate).diff(dayjs(startDate), 'day') : '',
                      // Automatically set to fixed trip when both dates are selected
                      trip_type: startDate && endDate ? 'fixed' : prev.trip_type
                    }));
                  }}
                />
                {form.start_date && form.end_date && (
                  <Box sx={{ 
                    mt: 1, 
                    p: 2, 
                    bgcolor: 'primary.50', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'primary.200'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      ✈️ {t('wizard_trip_duration')} {dayjs(form.end_date).diff(dayjs(form.start_date), 'day')} {t('wizard_day_plural')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('wizard_from')} {dayjs(form.start_date).format('MMM DD, YYYY')} {t('wizard_to')} {dayjs(form.end_date).format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('wizard_departure_month')}</Typography>
                  <TextField
                    select
                    value={form.start_month || ''}
                    required
                    onChange={e => setForm(prev => ({ ...prev, start_month: e.target.value }))}
                    sx={{ width: '100%' }}
                  >
                    <MenuItem value="">{t('wizard_select')}</MenuItem>
                    {generateMonthOptions(24).map(m => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('wizard_trip_duration')}</Typography>
                  <TextField
                    select
                    value={form.duration_days}
                    required
                    onChange={e => setForm(prev => ({ ...prev, duration_days: e.target.value }))}
                    sx={{ width: '100%' }}
                  >
                    <MenuItem value="">{t('wizard_select')}</MenuItem>
                    <MenuItem value="3">{t('wizard_duration_3days')}</MenuItem>
                    <MenuItem value="5">{t('wizard_duration_5days')}</MenuItem>
                    <MenuItem value="7">{t('wizard_duration_1week')}</MenuItem>
                    <MenuItem value="14">{t('wizard_duration_2weeks')}</MenuItem>
                    <MenuItem value="21">{t('wizard_duration_3weeks')}</MenuItem>
                    <MenuItem value="30">{t('wizard_duration_1month')}</MenuItem>
                    <MenuItem value="31">{t('wizard_duration_month_plus')}</MenuItem>
                  </TextField>
                </Box>
              </Box>
            </>
          )}

          </div>
          <div className="tw-flex tw-justify-between tw-px-6 tw-py-4 tw-border-t tw-border-surface-border tw-flex-shrink-0">
            <button
              type="button"
              onClick={prevStep}
              className="tw-px-5 tw-py-2.5 tw-rounded-xl tw-border tw-border-surface-border tw-text-sm tw-text-muted tw-transition-all tw-duration-200 tw-hover:tw-text-muted-fore tw-hover:tw-bg-surface-3"
            >
              {t('wizard_back')}
            </button>
            <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
              {step4ValidationMessage && (
                <Typography variant="caption" color="error">{step4ValidationMessage}</Typography>
              )}
              <button
                type="button"
                onClick={handleStep4Next}
                disabled={!canAdvanceStep4}
                className="tw-px-6 tw-py-2.5 tw-rounded-xl tw-bg-accent tw-text-white tw-font-semibold tw-text-sm tw-transition-all tw-duration-200 tw-hover:tw-brightness-110 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
              >
                {t('wizard_next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE MODE - Step 5: Logistics Chat (Redesigned) */}
      {actualMode === 'create' && step === 5 && (
        <div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-surface-border" style={{ backgroundColor: '#0d1117', height: '100%', minHeight: '500px', flex: 1 }}>
          {/* Chat Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b" style={{ backgroundColor: '#161b22', borderColor: 'rgba(99,102,241,0.25)' }}>
            <div className="tw-flex tw-items-center tw-gap-2">
              <GeminiIcon sx={{ color: '#818cf8' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#e6edf3' }}>{t('wizard_ai_assistant_title')}</Typography>
            </div>
            <button type="button" onClick={prevStep} className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-text-muted tw-border tw-border-surface-border tw-transition-all tw-duration-150 tw-hover:tw-text-muted-fore tw-hover:tw-bg-surface-3">{t('wizard_back')}</button>
          </div>

          {/* Chat History */}
          <Box 
            ref={logisticChatContainerRef}
            sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2 
            }}
          >
            {/* Render History Messages */}
            {logisticChatEntries.map((entry, idx) => (
              <Box
                key={`${entry.sender}-${idx}`}
                sx={{
                  alignSelf: entry.sender === 'ai' ? 'flex-start' : 'flex-end',
                  maxWidth: '80%',
                  bgcolor: entry.sender === 'ai' ? '#21262d' : 'rgba(99,102,241,0.18)',
                  color: '#c9d1d9',
                  p: 1.5,
                  borderRadius: 2,
                  border: entry.sender === 'ai' ? '1px solid rgba(99,102,241,0.15)' : '1px solid rgba(99,102,241,0.35)',
                  position: 'relative'
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontSize: '0.7rem' }}>
                  {entry.sender === 'ai' ? t('wizard_ai_name') : t('wizard_you')}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', direction: 'auto', unicodeBidi: 'plaintext', textAlign: 'start' }}>{entry.text}</Typography>
              </Box>
            ))}

            {/* Active Question Options (In-Chat) */}
            {currentLogisticQuestion && !logisticCompleted && (
              <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%', ml: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(currentLogisticQuestion.options || []).map((option, index) => {
                    const letter = LOGISTIC_OPTION_LETTERS[index] || String.fromCharCode(65 + index);
                    // Strip existing letter prefix like "A)" or "A." from the option text
                    const cleanOption = option.replace(/^[A-Za-z][\.\)\:]\s*/i, '');
                    return (
                      <Chip 
                        key={letter}
                        label={`${letter}. ${cleanOption}`}
                        onClick={() => {
                          // Immediate submission logic
                          const answerText = `${letter}) ${option}`;
                          const answeredEntry = {
                            questionId: currentLogisticQuestion.id,
                            question: currentLogisticQuestion.question,
                            country: currentLogisticQuestion.country,
                            answer: answerText,
                            letters: [letter],
                            note: ''
                          };
                          const updatedHistory = [...logisticHistory, answeredEntry];
                          setLogisticHistory(updatedHistory);
                          setLogisticChatEntries(prev => [...prev, { sender: 'user', text: answerText }]);
                          setCurrentLogisticQuestion(null);
                          setSelectedLogisticLetters([]);
                          setLogisticCustomResponse('');

                          if (updatedHistory.length >= MAX_LOGISTIC_INTERACTIONS) {
                            setLogisticCompleted(true);
                            setManualFollowUpAvailable(false);
                          } else if (updatedHistory.length >= AUTO_INTERACTION_CUTOFF) {
                            setLogisticCompleted(true);
                            setManualFollowUpAvailable(updatedHistory.length < MAX_LOGISTIC_INTERACTIONS);
                          } else {
                            fetchLogisticQuestion(updatedHistory);
                          }
                        }}
                        color="primary"
                        variant="outlined"
                        clickable
                        className="logistic-option-chip"
                        sx={{ 
                          bgcolor: '#21262d',
                          border: '1px solid',
                          borderColor: 'primary.main',
                          width: '100%',
                          justifyContent: 'flex-start',
                          color: '#c9d1d9',
                          '& .MuiChip-label': { color: '#c9d1d9' },
                          '&:hover': { bgcolor: 'rgba(99,102,241,0.12)' }
                        }}
                      />
                    );
                  })}
                </Box>
                
                {/* Select All / Select None buttons */}
                {(currentLogisticQuestion.options || []).length > 1 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-start' }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        // Select All and immediately submit
                        const allLetters = (currentLogisticQuestion.options || []).map((_, i) => 
                          LOGISTIC_OPTION_LETTERS[i] || String.fromCharCode(65 + i)
                        );
                        const answerText = formatLogisticAnswerText(allLetters, '', currentLogisticQuestion);
                        const answeredEntry = {
                          questionId: currentLogisticQuestion.id,
                          question: currentLogisticQuestion.question,
                          country: currentLogisticQuestion.country,
                          answer: answerText,
                          letters: allLetters,
                          note: ''
                        };
                        const updatedHistory = [...logisticHistory, answeredEntry];
                        setLogisticHistory(updatedHistory);
                        setLogisticChatEntries(prev => [...prev, { sender: 'user', text: answerText }]);
                        setCurrentLogisticQuestion(null);
                        setSelectedLogisticLetters([]);
                        setLogisticCustomResponse('');

                        if (updatedHistory.length >= MAX_LOGISTIC_INTERACTIONS) {
                          setLogisticCompleted(true);
                          setManualFollowUpAvailable(false);
                        } else if (updatedHistory.length >= AUTO_INTERACTION_CUTOFF) {
                          setLogisticCompleted(true);
                          setManualFollowUpAvailable(updatedHistory.length < MAX_LOGISTIC_INTERACTIONS);
                        } else {
                          fetchLogisticQuestion(updatedHistory);
                        }
                      }}
                      sx={{ 
                        fontSize: '0.75rem', 
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 1,
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      {t('wizard_select_all') || 'Select All'}
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        // Select None and immediately submit
                        const answerText = t('wizard_none_selected') || 'None of the above';
                        const answeredEntry = {
                          questionId: currentLogisticQuestion.id,
                          question: currentLogisticQuestion.question,
                          country: currentLogisticQuestion.country,
                          answer: answerText,
                          letters: [],
                          note: answerText
                        };
                        const updatedHistory = [...logisticHistory, answeredEntry];
                        setLogisticHistory(updatedHistory);
                        setLogisticChatEntries(prev => [...prev, { sender: 'user', text: answerText }]);
                        setCurrentLogisticQuestion(null);
                        setSelectedLogisticLetters([]);
                        setLogisticCustomResponse('');

                        if (updatedHistory.length >= MAX_LOGISTIC_INTERACTIONS) {
                          setLogisticCompleted(true);
                          setManualFollowUpAvailable(false);
                        } else if (updatedHistory.length >= AUTO_INTERACTION_CUTOFF) {
                          setLogisticCompleted(true);
                          setManualFollowUpAvailable(updatedHistory.length < MAX_LOGISTIC_INTERACTIONS);
                        } else {
                          fetchLogisticQuestion(updatedHistory);
                        }
                      }}
                      sx={{ 
                        fontSize: '0.75rem', 
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 1,
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      {t('wizard_select_none') || 'Select None'}
                    </Button>
                  </Box>
                )}
                
                {/* Combination Options (A & B, B & C, A & C for 3+ options) */}
                {(currentLogisticQuestion.options || []).length >= 3 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                    {(() => {
                      const letters = (currentLogisticQuestion.options || []).map((_, i) => 
                        LOGISTIC_OPTION_LETTERS[i] || String.fromCharCode(65 + i)
                      );
                      const combinations = [];
                      
                      // Generate all 2-letter combinations
                      for (let i = 0; i < letters.length - 1; i++) {
                        for (let j = i + 1; j < letters.length; j++) {
                          combinations.push([letters[i], letters[j]]);
                        }
                      }
                      
                      return combinations.map((combo, idx) => (
                        <Button
                          key={`combo-${idx}`}
                          size="small"
                          variant="text"
                          onClick={() => {
                            const answerText = formatLogisticAnswerText(combo, '', currentLogisticQuestion);
                            const answeredEntry = {
                              questionId: currentLogisticQuestion.id,
                              question: currentLogisticQuestion.question,
                              country: currentLogisticQuestion.country,
                              answer: answerText,
                              letters: combo,
                              note: ''
                            };
                            const updatedHistory = [...logisticHistory, answeredEntry];
                            setLogisticHistory(updatedHistory);
                            setLogisticChatEntries(prev => [...prev, { sender: 'user', text: answerText }]);
                            setCurrentLogisticQuestion(null);
                            setSelectedLogisticLetters([]);
                            setLogisticCustomResponse('');

                            if (updatedHistory.length >= MAX_LOGISTIC_INTERACTIONS) {
                              setLogisticCompleted(true);
                              setManualFollowUpAvailable(false);
                            } else if (updatedHistory.length >= AUTO_INTERACTION_CUTOFF) {
                              setLogisticCompleted(true);
                              setManualFollowUpAvailable(updatedHistory.length < MAX_LOGISTIC_INTERACTIONS);
                            } else {
                              fetchLogisticQuestion(updatedHistory);
                            }
                          }}
                          sx={{ 
                            fontSize: '0.75rem', 
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 1,
                            color: 'text.secondary',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          {combo.join(' & ')}
                        </Button>
                      ));
                    })()}
                  </Box>
                )}
              </Box>
            )}

            {/* Loading State */}
            {(logisticLoading || logisticSummaryLoading) && (
              <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderRadius: 2, bgcolor: '#161b22', border: '1px solid rgba(99,102,241,0.2)' }}>
                <CircularProgress size={14} sx={{ color: '#818cf8' }} />
                <Typography variant="caption" sx={{ color: '#8b949e' }}>{t('wizard_ai_typing')}</Typography>
              </Box>
            )}

            {/* Summary Card (Final AI Message) */}
            {logisticCompleted && (
              <Paper elevation={1} sx={{ alignSelf: 'flex-start', maxWidth: '90%', p: 2, borderRadius: 2, bgcolor: '#161b22', border: '1px solid rgba(99,102,241,0.2)', mt: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>{t('wizard_chat_summary')}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('wizard_plan_travel_to')} {destinationLabel} {t('wizard_for')} {durationLabel}. {t('wizard_logistics_collected')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(99,102,241,0.08)', borderRadius: 1, fontStyle: 'italic', borderLeft: '4px solid #6366f1' }}>
                  {logisticSummary || logisticContextSummary || t('wizard_no_details')}
                </Typography>
                
                {logisticSummaryError && (
                   <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>{t('wizard_error')} {logisticSummaryError}</Typography>
                )}

                <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('wizard_chat_prompt')}
                </Typography>
                
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth 
                  onClick={nextStep}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  {t('wizard_build_plan')}
                </Button>
              </Paper>
            )}
            
            {/* Invisible div to scroll to bottom */}
            <div ref={el => el && el.scrollIntoView({ behavior: 'smooth' })} />
          </Box>

          {/* Bottom Input Area */}
          <Box sx={{ p: 2, bgcolor: '#161b22', borderTop: '1px solid rgba(99,102,241,0.2)' }}>
            {/* Input Field Row */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={3}
                size="small"
                placeholder={logisticCompleted ? t('wizard_chat_placeholder') : t('wizard_chat_input_placeholder')}
                value={logisticCompleted ? additionalLogisticQuery : logisticCustomResponse}
                onChange={(e) => logisticCompleted ? setAdditionalLogisticQuery(e.target.value) : setLogisticCustomResponse(e.target.value)}
                disabled={logisticLoading || logisticSummaryLoading}
                sx={{ bgcolor: '#21262d', borderRadius: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,102,241,0.25)' }, '& textarea': { color: '#000000' } }}
              />
              <Button 
                variant="contained"
                color="primary" 
                onClick={logisticCompleted ? handleAdditionalLogisticQuery : handleLogisticResponse}
                disabled={logisticLoading || logisticSummaryLoading || (!logisticCompleted && !selectedLogisticLetters.length && !logisticCustomResponse.trim())}
                sx={{ minWidth: 'auto', px: 2, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: '#21262d', color: '#6e7681' } }}
              >
                <SendIcon />
              </Button>
            </Box>
          </Box>
        </div>
      )}

      {/* CREATE MODE - Step 3: Pace & Interests */}
      {actualMode === 'create' && step === 3 && (
        <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
          <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
          {renderStepHeader(t('wizard_step3_title'))}
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('wizard_travel_pace')}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {PACE_OPTIONS.map(opt => (
                <Box
                  key={opt.value}
                  role="button"
                  onClick={() => handleTravelPaceChange(opt.value)}
                  sx={{
                    cursor: 'pointer',
                    flex: 1,
                    p: 2,
                    textAlign: 'center',
                    borderRadius: 1,
                    border: form.travel_pace === opt.value ? '2px solid #6366f1' : '1px solid rgba(99,102,241,0.2)',
                    bgcolor: form.travel_pace === opt.value ? '#2d333b' : '#21262d',
                    color: form.travel_pace === opt.value ? '#818cf8' : '#8b949e',
                    transition: 'all 0.15s ease',
                    boxShadow: form.travel_pace === opt.value ? '0 0 0 1px rgba(99,102,241,0.25)' : 'none',
                    '&:hover': {
                      bgcolor: '#2d333b',
                      borderColor: 'rgba(99,102,241,0.5)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: form.travel_pace === opt.value ? 700 : 400, color: 'inherit' }}>{opt.label}</Typography>
                </Box>
              ))}
            </Box>
            {selectedPaceDetails && (
              <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid rgba(99,102,241,0.25)', bgcolor: 'rgba(99,102,241,0.06)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>{selectedPaceDetails.title}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{selectedPaceDetails.description}</Typography>
              </Box>
            )}
          </Box>
          
          <Box sx={{ mb: 2, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('wizard_main_interest')}</Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)' },
              gap: 1,
              width: '100%',
              minWidth: 0,
              overflow: 'hidden'
            }}>
              {INTEREST_CATEGORIES.map(item => {
                const selected = form.trip_interests.includes(item.code);
                return (
                  <Tooltip key={item.code} title={item.description} arrow>
                    <Box
                      role="button"
                      aria-label={item.description}
                      onClick={() => handleInterestChange(item.code)}
                      sx={{
                        cursor: 'pointer',
                        p: 1.25,
                        textAlign: 'center',
                        borderRadius: 1,
                        border: selected ? '2px solid #6366f1' : '1px solid rgba(99,102,241,0.2)',
                        bgcolor: selected ? '#2d333b' : '#21262d',
                        color: selected ? '#818cf8' : '#8b949e',
                        transition: 'all 0.15s ease',
                        boxShadow: selected ? '0 0 0 1px rgba(99,102,241,0.25)' : 'none',
                        '&:hover': { bgcolor: '#2d333b', borderColor: 'rgba(99,102,241,0.5)' }
                      }}
                    >
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: selected ? 700 : 400, color: 'inherit', lineHeight: 1.2 }}>{item.label}</Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
            {interestHint && (
              <Box sx={{ mt: 1, p: 1.25, borderRadius: 1, border: '1px dashed rgba(99,102,241,0.3)', bgcolor: 'rgba(99,102,241,0.06)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{interestHint}</Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              {t('wizard_interests_hover')}
            </Typography>
          </Box>
          
          </div>
          <div className="tw-flex tw-justify-between tw-px-6 tw-py-4 tw-border-t tw-border-surface-border tw-flex-shrink-0">
            <button
              type="button"
              onClick={prevStep}
              className="tw-px-5 tw-py-2.5 tw-rounded-xl tw-border tw-border-surface-border tw-text-sm tw-text-muted tw-transition-all tw-duration-200 tw-hover:tw-text-muted-fore tw-hover:tw-bg-surface-3"
            >
              {t('wizard_back')}
            </button>
            <button
              type="button"
              onClick={handleStep3Next}
              disabled={!canAdvanceStep3}
              className="tw-px-6 tw-py-2.5 tw-rounded-xl tw-bg-accent tw-text-white tw-font-semibold tw-text-sm tw-transition-all tw-duration-200 tw-hover:tw-brightness-110 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
            >
              {t('wizard_next')}
            </button>
          </div>
        </div>
      )}

      {/* CREATE MODE - Step 6: Interactive Builder */}
      {actualMode === 'create' && step === 6 && builderSubmitting && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 3 }}>
          <CircularProgress size={56} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {t('building_your_trip') || 'Building your trip...'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            {t('preparing_itinerary') || 'Preparing your personalized itinerary'}
          </Typography>
        </Box>
      )}
      {actualMode === 'create' && step === 6 && !builderSubmitting && (
        <InteractiveRouteBuilder
          tripData={{
            ...form,
            destination_countries: (form.selectedCountriesOrAirports || [])
              .filter(item => item.type === 'country')
              .map(item => item.label || item.value || item.name)
          }}
          fullSuggestion={groupDaysIntoBlocks(previewRoute)}
          cityContexts={cityContexts}
          isLoading={isPreviewing}
          logisticSummary={logisticSummary}
          onError={(error) => {
            setPreviewError(error?.message || 'An error occurred');
          }}
          onComplete={(confirmedBlocks) => {
            // Immediately show loading screen to prevent flash of original plan
            setBuilderSubmitting(true);
            // Store blocks synchronously via ref, also update state
            confirmedBlocksFromBuilderRef.current = confirmedBlocks;
            setConfirmedBlocksFromBuilder(confirmedBlocks);
            confirmAndSubmit();
          }}
          language={i18n.language}
        />
      )}

      {/* Step 7 removed - InteractiveRouteBuilder now has its own summary screen 
          and directly calls confirmAndSubmit() when user clicks "Continue to Activities Planning" */}

      {/* UPDATE MODE - Full Featured Edit Interface */}
      {actualMode === 'update' && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>{t('wizard_edit_trip')}</Typography>
          
          {/* Trip Name */}
          <TextField
            fullWidth
            label={t('wizard_trip_name')}
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
          />
          
          {/* Countries Selection */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>{t('wizard_countries_visit')}</Typography>
            <TextField
              fullWidth
              label={t('wizard_search_countries_airports')}
              value={countryAirportInput}
              onChange={handleCountryAirportInput}
              onFocus={handleCountryAirportFocus}
              margin="normal"
            />
            
            {countryAirportOptions.length > 0 && countryDropdownOpen && (
              <Paper sx={{ maxHeight: 200, overflowY: 'auto', mt: 1 }}>
                <List>
                  {countryAirportOptions.map((option, idx) => (
                    <ListItem 
                      button 
                      key={`${option.value}-${idx}`} 
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectCountryAirport(option)}
                    >
                      <ListItemText 
                        primary={option.label}
                        secondary={option.type === 'country' ? t('wizard_country') : option.type === 'city' ? t('wizard_city') : t('wizard_airport')}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Selected Countries Display */}
            {form.selectedCountriesOrAirports.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('wizard_selected_countries_airports')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {form.selectedCountriesOrAirports.map((item, index) => (
                    <Chip
                      key={`${item.value}-${index}`}
                      label={item.label}
                      onDelete={() => removeCountryOrAirport(index)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Airport Selection */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>{t('wizard_flight_details')}</Typography>
            
            {/* Home Airport */}
            <Typography variant="subtitle1" gutterBottom>{t('wizard_home_airport_departure')}</Typography>
            <TextField
              fullWidth
              label={t('wizard_search_home_airport')}
              value={homeAirportInput}
              onChange={handleHomeAirportInput}
              onFocus={handleHomeAirportFocus}
              margin="normal"
            />
            
            {homeAirportOptions.length > 0 && homeDropdownOpen && (
              <Paper sx={{ maxHeight: 200, overflowY: 'auto', mt: 1, bgcolor: '#21262d', border: '1px solid rgba(99,102,241,0.25)' }}>
                <List>
                  {homeAirportOptions.map((airport, idx) => (
                    <ListItem button key={`home-${airport.value}-${idx}`} onClick={() => handleSelectHomeAirport(airport)}>
                      <ListItemText 
                        primary={airport.label}
                        secondary={t('wizard_home_airport')}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {form.homeAirport && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(99,102,241,0.1)', borderRadius: 1, border: '1px solid rgba(99,102,241,0.25)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('wizard_selected_home_airport')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body1">{form.homeAirport.label}</Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setForm(prev => ({ ...prev, homeAirport: null }))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Arrival Airport */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>{t('wizard_arrival_airport_first_dest')}</Typography>
            <TextField
              fullWidth
              label={t('wizard_search_arrival_airport')}
              value={arrivalAirportInput}
              onChange={handleArrivalAirportInput}
              onFocus={handleArrivalAirportFocus}
              margin="normal"
            />
            
            {arrivalAirportOptions.length > 0 && arrivalDropdownOpen && (
              <Paper sx={{ maxHeight: 200, overflowY: 'auto', mt: 1, bgcolor: '#21262d', border: '1px solid rgba(99,102,241,0.25)' }}>
                <List>
                  {arrivalAirportOptions.map((airport, idx) => (
                    <ListItem 
                      button 
                      key={`arrival-${airport.value}-${idx}`} 
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectArrivalAirport(airport)}
                    >
                      <ListItemText 
                        primary={airport.label}
                        secondary={t('wizard_arrival_airport')}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {form.arrivalAirport && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgb(50,50,180)', borderRadius: 1, border: '1px solid rgba(150,150,220,0.35)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('wizard_selected_arrival_airport')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body1">{form.arrivalAirport.label}</Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setForm(prev => ({ ...prev, arrivalAirport: null }))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Return Airport */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>{t('wizard_return_airport_last_dest')}</Typography>
            <TextField
              fullWidth
              label={t('wizard_search_return_airport')}
              value={returnAirportInput}
              onChange={handleReturnAirportInput}
              onFocus={handleReturnAirportFocus}
              margin="normal"
            />
            
            {returnAirportOptions.length > 0 && returnDropdownOpen && (
              <Paper sx={{ maxHeight: 200, overflowY: 'auto', mt: 1, bgcolor: '#21262d', border: '1px solid rgba(99,102,241,0.25)' }}>
                <List>
                  {returnAirportOptions.map((airport, idx) => (
                    <ListItem 
                      button 
                      key={`return-${airport.value}-${idx}`} 
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectReturnAirport(airport)}
                    >
                      <ListItemText 
                        primary={airport.label}
                        secondary={t('wizard_return_airport')}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {form.returnAirport && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(245,158,11,0.1)', borderRadius: 1, border: '1px solid rgba(245,158,11,0.25)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('wizard_selected_return_airport')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body1">{form.returnAirport.label}</Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setForm(prev => ({ ...prev, returnAirport: null }))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>

          {/* Trip Duration and Dates */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>{t('wizard_trip_duration_timing')}</Typography>
            
            {/* Trip Type */}
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('wizard_trip_type')}</InputLabel>
              <Select
                value={form.trip_type}
                onChange={(e) => setForm(prev => ({ ...prev, trip_type: e.target.value }))}
              >
                <MenuItem value="flexible">{t('wizard_flexible_dates')}</MenuItem>
                <MenuItem value="fixed">{t('wizard_fixed_dates')}</MenuItem>
              </Select>
            </FormControl>

            {/* Duration */}
            <TextField
              fullWidth
              label={t('wizard_duration_days')}
              type="number"
              value={form.duration_days}
              onChange={(e) => setForm(prev => ({ ...prev, duration_days: parseInt(e.target.value) || 7 }))}
              margin="normal"
              inputProps={{ min: 1, max: 365 }}
            />

            {/* Conditional Date Fields */}
            {form.trip_type === 'fixed' ? (
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  type="date"
                  label={t('wizard_start_date')}
                  value={form.start_date}
                  onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="date"
                  label={t('wizard_end_date')}
                  value={form.end_date}
                  onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('wizard_preferred_month')}</InputLabel>
                <Select
                  value={form.start_month}
                  onChange={(e) => setForm(prev => ({ ...prev, start_month: e.target.value }))}
                >
                  {generateMonthOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          
          {/* Travel Pace */}
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('wizard_travel_pace')}</InputLabel>
            <Select
              value={form.travel_pace}
              onChange={(e) => handleTravelPaceChange(e.target.value)}
            >
              {PACE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.menuLabel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Interests */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{t('wizard_trip_interests')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {INTEREST_ITEMS.map(interest => (
                <Chip
                  key={interest.code}
                  label={interest.label}
                  color={form.trip_interests.includes(interest.code) ? 'primary' : 'default'}
                  onClick={() => handleInterestChange(interest.code)}
                />
              ))}
            </Box>
          </Box>
          
          {/* Current Itinerary */}
          {previewRoute && previewRoute.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>{t('wizard_current_itinerary')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {previewRoute.map((day, index) => (
                  <Paper key={day.day} sx={{ p: 2 }}>
                    <Typography variant="body1">
                      <strong>Day {day.day}:</strong> {day.city}, {day.country}
                    </Typography>
                  </Paper>
                ))}
              </Box>
              
              {/* AI Revision */}
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label={t('wizard_request_changes_placeholder')}
                  value={revisionInstructions}
                  onChange={(e) => setRevisionInstructions(e.target.value)}
                  margin="normal"
                  multiline
                  rows={2}
                />
                <Button
                  variant="outlined"
                  onClick={() => handleRegenerate()}
                  disabled={isPreviewing || !revisionInstructions.trim()}
                  sx={{ mt: 1 }}
                >
                  {isPreviewing ? <CircularProgress size={20} /> : t('wizard_apply_changes')}
                </Button>
              </Box>
            </Box>
          )}
          
          {/* Generate New Itinerary Button */}
          {!previewRoute && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => handlePreview()}
                disabled={isPreviewing || !form.selectedCountriesOrAirports.length}
                fullWidth
              >
                {isPreviewing ? <CircularProgress size={20} /> : t('wizard_generate_new_itinerary')}
              </Button>
            </Box>
          )}
          
          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!form.name}
            >
              {t('wizard_save_changes')}
            </Button>
            {onCancel && (
              <Button
                variant="outlined"
                onClick={() => {
                  try { if (typeof onCancel === 'function') onCancel(); } catch (e) { /* ignore */ }
                  // Redirect back to the home page to abort creation
                  window.location.href = '/travel2/';
                }}
              >
                {t('cancel')}
              </Button>
            )}
          </Box>
        </Box>
      )}
      {/* Add City Dialog */}
      <Dialog open={addCityOpen} onClose={() => setAddCityOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('wizard_add_city_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            {t('wizard_add_city_description')}
          </Typography>
          <Grid container spacing={1} sx={{ mt: 1, maxHeight: '50vh', overflowY: 'auto' }}>
            {addCityCandidates && addCityCandidates.length > 0 ? (
              addCityCandidates.map((c, idx) => (
                <Grid item xs={12} sm={6} key={`${c.city}-${idx}`}>
                  <Paper sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 'bold' }}>{c.city}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.country}</Typography>
                    </Box>
                    <Box>
                      <Button size="small" variant="contained" onClick={() => handleAddCity(c)} disabled={isPreviewing}>{t('add')}</Button>
                    </Box>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('wizard_no_candidate_cities')}</Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCityOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
        </form>
      </div>
    </div>
  );
}

export default TripWizard;
